import { lockBannerCopy, makeStepTitleResolver } from '../lib/step-lock-copy';
import { Suspense, forwardRef, lazy, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createSaveOrchestrator } from '../lib/stepSaveOrchestrator';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApplications, useAsync, useSteps, useHydratedStep, useStepAccess, useIdentityAnnotations, identityAnnotationsKey } from '../hooks/usePortal';
import { useIsMobile } from '../hooks/useIsMobile';
import { usePortalContext } from '../hooks/usePortalContext';
import {
  portal,
  drafts,
  uploadDocument,
  uploadSkillCertification,
  fetchDocumentObject,
  uploadShowcaseMedia,
  deleteShowcaseMedia,
  deleteStepMedia,
  stepMediaIdFromUrl,
  uploadProfilePhoto,
  toEmbassyOptions,
  injectEmbassyOptions,
} from '../lib/portal';
import { useCbcUploadLifecycle } from '../hooks/useCbcUploadLifecycle';
import { usePassportCapture } from '../hooks/usePassportCapture';
import { canonicalPassportTargets, prepareCanonicalPassportSubmission } from '../lib/passportCanonicalExtraction';
import { useCurrentScope } from '../hooks/useCurrentScope';
import { engineHelpers } from '../lib/formEngineHelpers';
export { shouldSyncPrimaryPassportName, syncPassportNameAfterOcr } from '../lib/passportNameSync';
import { IdentityAnnotationsNotice } from '../components/IdentityAnnotationsNotice';
import {
  normaliseSteps,
  isDone,
  needsAttention,
  needsReviewAttention,
  isCompleteForApplicant,
  isNavigationComplete,
  needsNavigationAttention,
  isReferenceStep,
  NormalStep,
  applyStepAccess,
  isReadOnly,
  canSubmitStep,
  isStepLockedError,
  classifySaveError,
  groupSteps,
  resolvePhaseSection,
  sectionKeyOf,
  phaseGroupingOf,
  type PhaseSection,
  isHubStep,
  isSubtaskStep,
  progress,
} from '../lib/steps';
import type { ReviewFeedbackItem } from '../lib/portal';
import { useLanguageSupport } from '../lib/languageAnalysis';
import { useCbcStatus } from '../lib/cbcTranslation';
import { CBC_PROCESSING_DELAYED_NOTICE } from '../lib/cbcUploadLifecycle';
import { CbcTranslationFallbackCard } from '../components/CbcTranslationFallbackCard';
import {
  isProofOfStudentStatusStep,
  useSwtAcademicVerificationStatus,
  useSwtEnrollmentProofStatus,
} from '../lib/swtStatus';
import SwtStatusPanel from '../components/SwtStatusPanel';
import { track } from '../lib/analytics';
import { auth } from '../lib/api';
import { useResolvedScheme } from '../lib/theme';
import ShowcaseYourself, { type ShowcaseValue, type PhotoRef } from '../components/ShowcaseYourself';
import ReferenceDetailsStep from '../components/ReferenceDetailsStep';
import { InfoFieldWithTemplateDownload } from '../components/InfoFieldWithTemplateDownload';
import { bindApplicationTemplateDownloads } from '../lib/application-template-download';
import ApplicationStepperNav from '../components/ApplicationStepperNav';

// The engine is lazy-loaded to keep its bundle out of the initial payload. We
// wrap it in the engine's own DataBoundaryProvider — resolved from the SAME
// dynamic import so nothing leaks into the main chunk — so AI text-assist
// fields (type `chatgpt_assistant`: the "Enhance wording" button + "AI
// Suggestions" box under a description) route their run('aiSuggestions') calls
// to our BFF. Without this the engine falls back to a same-origin GLOBUS
// default (POST /api/openai/suggestions) that 404s on the split-origin,
// Bearer-auth portal, so the suggestions box silently never appears.
/** The application the user is currently working in. StepRenderer keeps it
 *  fresh; the engine adapter's fetchImpl stamps it onto every flat document
 *  upload as X-Application-Id so the BFF never has to guess which application
 *  a file belongs to (guessing previously filed uploads onto old terminal
 *  applications when the upstream applications list was stale). */
let engineUploadApplicationId: string | null = null;

/** fetch wrapper for the engine's data boundary: adds the current application
 *  id to POST /api/v1/documents requests, passes everything else through.
 *  (Language detection/translation is NOT triggered here anymore: the Hub's
 *  server-side pipeline detects + translates after step submit — see
 *  lib/cbcTranslation.ts.) */
/** Test-only hooks for the upload interceptor (no runtime consumers). */
export const __uploadInterceptorForTest = {
  setApplicationId(id: string | null) {
    engineUploadApplicationId = id;
  },
  fetch: (input: RequestInfo | URL, init?: RequestInit) => engineFetch(input, init),
};

const engineFetch: typeof fetch = (input, init) => {
  const request = typeof input === 'string' || input instanceof URL ? null : input;
  const url = request ? request.url : input.toString();
  // Per fetch semantics, init overrides the Request's own fields when both
  // are given — fall back to the Request for anything init omits.
  const method = (init?.method ?? request?.method ?? 'GET').toUpperCase();
  if (engineUploadApplicationId && method === 'POST' && url.split('?')[0].endsWith('/api/v1/documents')) {
    // Start from the effective headers (init overrides the Request's), then
    // stamp only X-Application-Id so auth/content headers are preserved.
    const headers = new Headers(init?.headers ?? request?.headers);
    headers.set('X-Application-Id', engineUploadApplicationId);
    return fetch(input, { ...init, headers });
  }
  return fetch(input, init);
};

const FormEnginePortable = lazy(async () => {
  const m: any = await import('@alliance-strategies/form-engine-portable');
  const Engine = (m.FormEnginePortable ?? m.default) as React.ComponentType<Record<string, unknown>>;
  const { DataBoundaryProvider, createGlobusAdapter, createEndpointRegistry } = m;
  // One adapter for the app's lifetime; getAuthHeader reads the live token per
  // call (survives refresh). Routes map the engine's logical AI ops to the
  // BFF's AiTextController (@Controller('ai/text'), global prefix api/v1).
  const adapter = createGlobusAdapter({
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
    credentials: 'same-origin',
    fetchImpl: engineFetch,
    getAuthHeader: () => {
      const t = auth.getToken();
      return t ? `Bearer ${t}` : undefined;
    },
    routes: createEndpointRegistry({
      // Every AI op a form-engine field invokes must be mapped to its BFF route,
      // else it falls back to the GLOBUS contract default (/api/integrations/…)
      // and 404s against our BFF. aiSuggestions = ChatGPTAssistantField's
      // "Enhance wording"; aiTextReview = TextareaWithAI's "Review my Description".
      aiSuggestions: { method: 'POST', path: '/api/v1/ai/text/suggestions' },
      aiTextReview: { method: 'POST', path: '/api/v1/ai/text/review' },
      // Document uploads. AdvancedFileUpload (every file_upload_advanced field:
      // passport, criminal background, degree/diploma, …) posts through the data
      // boundary as run('uploadFile'|'uploadMainDocument'). Without these the
      // engine falls back to the GLOBUS contract default (/api/integrations/…)
      // which 404s on our BFF, so the upload "fails". The engine doesn't post
      // an app id on this path, so engineFetch stamps X-Application-Id onto the
      // request and the BFF uses it (falling back to JWT resolution otherwise).
      uploadFile: { method: 'POST', path: '/api/v1/documents' },
      uploadMainDocument: { method: 'POST', path: '/api/v1/documents' },
    }),
  });
  const WithData: React.ComponentType<Record<string, unknown>> = (props) => (
    <DataBoundaryProvider adapter={adapter}>
      <Engine {...props} />
    </DataBoundaryProvider>
  );
  return { default: WithData };
});

// Applicant-facing copy for an agreement step whose hydration failed. The raw
// upstream envelope (e.g. {"code":"AGREEMENT_NOT_RESOLVABLE"}) must never be
// shown verbatim to an applicant.
const AGREEMENT_UNAVAILABLE_NOTICE =
  "This agreement isn't ready to sign yet — please try again shortly or contact your coordinator.";

/** Append the upstream error only when it reads like a short technical code
 *  (e.g. "AGREEMENT_NOT_RESOLVABLE") — never a JSON envelope or an HTML page. */
const shortTechnicalCode = (message: string | undefined): string | undefined =>
  message && message.length <= 64 && !/[{}<>"]/.test(message) ? message : undefined;

/**
 * My Application: phase tabs → step list → form-engine-portable rendering.
 * Auto-save goes to IndexedDB per step (AP-16); drafts merge under saved
 * Globus data on open (Globus wins); submit posts to the BFF step endpoint.
 * Globus messages render verbatim on the form banner and stepper badges (AP-1).
 */
export default function ApplicationPage() {
  const { active, loading, error } = useApplications();
  const isCurrentApplication = useCurrentScope(String(active?.id ?? ''));
  // Shared query cache: a dashboard prefetch (or a previous visit) renders the
  // form instantly; a cold visit fetches once and every re-navigation is free.
  const stepsState = useSteps(active?.id);
  // Step-access contract: the server's single authoritative access decision.
  // staleTime:0 re-validates on every mount; we also refetch on window focus
  // and visibility-change so a lock applied while the tab was backgrounded is
  // picked up the moment the applicant returns (Nexus section-locking contract).
  const { data: accessEntries, refetch: refetchAccess } = useStepAccess(active?.id);
  const allSteps = useMemo(() => normaliseSteps(stepsState.data), [stepsState.data]);
  const steps = useMemo(
    () => applyStepAccess(allSteps, accessEntries ?? []),
    [allSteps, accessEntries],
  );
  // Configured title lookup for prerequisite steps named in unlock_conditions
  // (UPSTREAM_STEP_INCOMPLETE). Resolved against the UNFILTERED list so a
  // prerequisite the applicant cannot currently see is still named correctly.
  // Facts may carry the unversioned key (`step.personal_information.status`)
  // while the workflow key is versioned (`personal_information_v1_0`).
  const resolveStepTitle = useMemo(() => makeStepTitleResolver(allSteps), [allSteps]);
  // Refresh the step-access snapshot on window focus and visibility-change so a
  // lock applied while the tab was backgrounded is picked up immediately.
  useEffect(() => {
    const onFocus = () => refetchAccess();
    const onVisible = () => { if (document.visibilityState === 'visible') refetchAccess(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refetchAccess]);
  // Post-save refresh: invalidate the full steps list, not just the submitted
  // step. A `.map()` patch of the cached array can only update an existing
  // row — it can never insert a step that just became visible because this
  // save's answer flipped a later step's condition (form-engine-portable's
  // FormEnginePortableProps.onSave JSDoc calls this out explicitly: the
  // engine has no view of the host's step list and can't refresh it for us).
  const queryClient = useQueryClient();
  const refreshStep = useCallback(
    async (_submittedKey: string) => {
      if (!active?.id || !isCurrentApplication()) return;
      void queryClient.invalidateQueries({ queryKey: ['journey', String(active.id)] });
      void queryClient.invalidateQueries({ queryKey: ['steps', String(active.id)] });
      // A confirmed save may have applied (or re-applied) passport identity
      // upstream — re-read the annotations Personal/Passport Information show.
      void queryClient.invalidateQueries({ queryKey: identityAnnotationsKey(active.id) });
      // Re-fetch the step-access snapshot after every submission: a step
      // completion can trigger a Section Lock policy, which would change the
      // access level of other steps. AWAITED so post-submit navigation lands
      // on FRESH lock state — a fast "Next" click must never render the next
      // step from the stale pre-submit snapshot. A refetch failure never
      // blocks navigation (the focus/visibility refetches self-heal it).
      try {
        await refetchAccess();
      } catch {
        /* navigation proceeds; focus/visibility refetch covers */
      }
      // The Hub can finalise submission side-effects (unlocks, section locks)
      // a beat after the submit response — one delayed re-read closes that
      // brief processing window without holding up the applicant.
      if (isCurrentApplication()) {
        window.setTimeout(() => {
          if (!isCurrentApplication()) return;
          void refetchAccess();
        }, 1500);
      }
    },
    [active?.id, queryClient, refetchAccess, isCurrentApplication],
  );
  // Documents that have PASSED review (staff-approved / marked official) lock
  // their upload field — the applicant can no longer remove or replace them.
  // Pending/rejected docs stay editable (the existing remove/re-upload). Globus
  // exposes no document delete, so pre-approval "remove" only unlinks the
  // reference; true storage-delete is a pending Globus/Hub ask.
  const docsState = useAsync(() => (active ? portal.documents(active.id) : Promise.resolve([])), [active?.id]);
  const approvedDocIds = useMemo(() => {
    const list = Array.isArray(docsState.data) ? docsState.data : [];
    const isApproved = (d: any) => d?.official === true || /approv|verif|accept/i.test(String(d?.status ?? ''));
    return new Set<string>(list.filter(isApproved).map((d: any) => String(d.id)));
  }, [docsState.data]);
  // Sub-task steps (nodeType === 'subtask_step') are hidden from the
  // top-level stepper — they are navigated via the hub checklist page
  // (onOpenSubTask callback). Hub steps themselves ARE included as single
  // stepper nodes (Asana 1216802150410999). Filtering happens BEFORE grouping
  // so neither grouping mode (bucket or legacy) ever shows a sub-task node.
  const stepperSteps = useMemo(() => steps.filter((s) => !isSubtaskStep(s)), [steps]);
  // The 3-phase portal contract: Globus's 8-position journey spine collapses to
  // exactly Apply / Documents & Compliance / Visa & Travel. Bucketing runs on the
  // ALREADY access-filtered list above, so server-hidden steps can never
  // resurrect a bucket. Empty buckets are dropped by groupSteps.
  const phases = useMemo(() => groupSteps(stepperSteps), [stepperSteps]);
  // Which grouping groupSteps chose — carried on analytics so a section id's
  // bimodal value space (bucket id vs raw phaseKey) stays separable downstream.
  const phaseGrouping = useMemo(() => phaseGroupingOf(stepperSteps), [stepperSteps]);

  // Deep-link from the dashboard's journey snake / progress banner.
  const linked = (useLocation().state ?? {}) as { stepKey?: string; phaseKey?: string; bucketId?: string };
  // Explicit selection (tab click / Back-Next / goTo). Undefined until the
  // applicant navigates, so the deep-link resolution below stays authoritative
  // for the initial render — including the renders after `steps` arrives, which
  // is when `phases` first becomes resolvable at all.
  const [phaseKey, setPhaseKey] = useState<string | undefined>(undefined);
  const [stepKey, setStepKey] = useState<string | undefined>(linked.stepKey);
  // A successful correction can race Globus's eventually-consistent
  // step-access projection. Remember the exact rejection that was resubmitted
  // for this page session so the stale top-level message is suppressed without
  // deleting its still-useful item feedback. A different rejection fingerprint
  // is a new review cycle and is shown normally.
  const [submittedRejections, setSubmittedRejections] = useState<Record<string, string>>({});
  // stepKey FIRST. Every emitter — pre- and post-PR — always includes stepKey,
  // and a stepKey is exact, grouping-mode independent and immune to the bucket
  // migration, so it beats both phase candidates. Only then the ladder, and the
  // two phase candidates are tried in SEQUENCE: eagerly picking one
  // (`bucketId ?? phaseKey`) discarded the other without ever trying it, which
  // defeated the point of emitting both.
  const linkedSection = useMemo(() => {
    if (phases.length === 0) return undefined;
    const target = linked.stepKey ? steps.find((s) => s.stepKey === linked.stepKey) : undefined;
    const viaStepKey = target ? sectionKeyOf(phases, target) : undefined;
    return (
      (viaStepKey ? phases.find(([k]) => k === viaStepKey) : undefined) ??
      resolvePhaseSection(phases, linked.bucketId) ??
      resolvePhaseSection(phases, linked.phaseKey)
    );
  }, [phases, steps, linked.stepKey, linked.bucketId, linked.phaseKey]);
  const phase = (phaseKey ? resolvePhaseSection(phases, phaseKey) : undefined) ?? linkedSection ?? phases[0];
  // `?? phases[0]` is a silent wrong-tab landing: a deep-link arrived that
  // nothing could resolve and the applicant sees tab 1 as if that were normal.
  // Instrument it once per mount so it stops being invisible.
  const warnedDeepLinkFallback = useRef(false);
  useEffect(() => {
    if (phases.length === 0 || warnedDeepLinkFallback.current) return;
    if (!linked.stepKey && !linked.bucketId && !linked.phaseKey) return; // no deep-link: tab 1 IS the default
    if ((phaseKey ? resolvePhaseSection(phases, phaseKey) : undefined) ?? linkedSection) return;
    warnedDeepLinkFallback.current = true;
    console.warn(
      `[application] unresolvable deep-link (stepKey=${linked.stepKey ?? '-'}, bucketId=${linked.bucketId ?? '-'}, ` +
        `phaseKey=${linked.phaseKey ?? '-'}) — falling back to the first phase "${phases[0]?.[0]}".`,
    );
    track('deep_link_phase_fallback', {
      applicationId: active?.id ?? null,
      stepKey: linked.stepKey ?? null,
      bucketId: linked.bucketId ?? null,
      requestedPhaseKey: linked.phaseKey ?? null,
      landedPhaseKey: phases[0]?.[0] ?? null,
      phaseGrouping,
    });
  }, [phases, phaseKey, linkedSection, linked.stepKey, linked.bucketId, linked.phaseKey, active?.id, phaseGrouping]);
  // Sub-task steps are filtered out of `phases` (hub wiring, Asana
  // 1216802150410999), so a stepKey set by the hub checklist's onOpenSubTask
  // resolves against the FULL step list — the phase sections can never contain
  // it. Ordinary keys resolve from the active section first, as before.
  const step =
    phase?.[1].steps.find((s) => s.stepKey === stepKey) ??
    (stepKey ? steps.find((s) => s.stepKey === stepKey && isSubtaskStep(s)) : undefined) ??
    phase?.[1].steps[0];
  const isCurrentNavigation = useCurrentScope(`${active?.id ?? ''}:${step?.stepKey ?? ''}`);
  // Once Globus reports that a step no longer needs action, retire this
  // page-session suppression marker. A later review cycle may legitimately use
  // the same rejection code/message and must be shown again.
  useEffect(() => {
    if (!step || step.needsAction) return;
    setSubmittedRejections((current) => {
      if (!(step.stepKey in current)) return current;
      const next = { ...current };
      delete next[step.stepKey];
      return next;
    });
  }, [step?.stepKey, step?.needsAction]);
  // Reference steps branch to ReferenceDetailsStep — one predicate, shared by
  // the render branches and the banner-impression effect below.
  const referenceStep = step ? isReferenceStep(step.stepKey) : false;

  // Agreement steps hydrate through the hook (placeholder→hydration→pin
  // contract: see GlobusService.getApplicationStepConfig). `renderStep` stays
  // undefined until the real config arrives — the placeholder never renders.
  const {
    renderStep,
    error: hydrationError,
    reload: reloadHydratedStep,
  } = useHydratedStep(step, active?.id);

  // Rejection-surface impressions (Asana 1216270468356070). reasonCode is null
  // by design on both events: steps' applicant_messages carry no reason code
  // today, so the measured null-rate is deliberate evidence for the upstream
  // reasonCode-on-steps ask.
  // Both effects wait for the step-access snapshot to resolve: until it does,
  // `steps` is built with `accessEntries ?? []`, so server-hidden steps are
  // still present — an impression fired then would report steps the applicant
  // can never see, and the fired-set would suppress the corrected event forever.
  // Step banner: fired once per application+step when the selected step renders
  // the globus-banner for a needs-attention status. Page-level (not in StepForm)
  // so the fired set survives StepForm remounts while navigating between steps;
  // keys are `${appId}:${stepKey}` because bare stepKeys collide across
  // applications. Reference steps are excluded with the SAME predicate the
  // render uses below — they branch to ReferenceDetailsStep and never render
  // the globusMessage banner.
  const firedBannerStepKeys = useRef(new Set<string>());
  useEffect(() => {
    if (accessEntries === undefined) return; // step-access snapshot not resolved yet
    if (!active?.id || !step?.globusMessage || !needsReviewAttention(step)) return;
    if (referenceStep) return;
    const firedKey = `${active.id}:${step.stepKey}`;
    if (firedBannerStepKeys.current.has(firedKey)) return;
    firedBannerStepKeys.current.add(firedKey);
    track('rejection_envelope_shown', {
      surface: 'step_banner',
      applicationId: active.id,
      stepKey: step.stepKey,
      reasonCode: null,
    });
  }, [active?.id, step, referenceStep, accessEntries]);
  // Stepper badge: the stepper renders ONLY the active phase's dots (the phase
  // tabs carry no attention indicator), so the impression covers exactly the
  // needs-attention steps of the currently selected phase — one aggregate event
  // per application+phase (NOT per node; the dots re-render constantly). A phase
  // with zero attention steps fires nothing and does NOT poison the fired-set,
  // so attention appearing later still emits.
  const stepperFiredPhases = useRef(new Set<string>());
  useEffect(() => {
    if (accessEntries === undefined) return; // step-access snapshot not resolved yet
    if (!active?.id || !phase) return;
    const [currentPhaseKey, currentPhase] = phase;
    const firedKey = `${active.id}:${currentPhaseKey}`;
    if (stepperFiredPhases.current.has(firedKey)) return;
    // Rejection impressions only — compliance feedback is not a staff
    // rejection and must not inflate this funnel event.
    const attention = currentPhase.steps.filter(needsReviewAttention);
    if (attention.length === 0) return;
    stepperFiredPhases.current.add(firedKey);
    track('rejection_envelope_shown', {
      surface: 'stepper_badge',
      applicationId: active.id,
      // The selected TAB's id — a portal bucket id under the 3-phase contract,
      // a raw Globus phaseKey in the legacy fallback. Property name kept so
      // existing PostHog queries on this event keep resolving; `phaseGrouping`
      // says WHICH value space this one is in, so cohorts don't silently
      // split (same phase, two ids) or merge (two phases, one id).
      phaseKey: currentPhaseKey,
      phaseGrouping,
      stepKeys: attention.map((s) => s.stepKey),
      count: attention.length,
      reasonCode: null,
    });
  }, [active?.id, phase, accessEntries, phaseGrouping]);

  const isMobile = useIsMobile();

  // Legal-name ↔ passport gate (form-engine-portable >=2.21): the legal-name
  // section ("as shown on your passport") must match the applicant's passport
  // EXACTLY or the engine blocks Save & Continue. Passport given names/surname
  // are OCR-extracted into passport_name/passport_surname on the passport step;
  // find whichever step holds them (across the whole workflow) and hand them to
  // the engine. Undefined (passport step not done) → engine no-op.
  // NOTE: this hook MUST stay above the early returns below — placing it after
  // them changes the hook count between the loading and loaded renders, which
  // crashes React with "rendered more hooks than during the previous render"
  // (#310) and white-screens the whole application page.
  const expectedPassportName = useMemo(() => {
    const s = steps.find((x) => x.stepData?.passport_name || x.stepData?.passport_surname);
    return s
      ? {
          givenNames: s.stepData?.passport_name as string | undefined,
          surname: s.stepData?.passport_surname as string | undefined,
        }
      : undefined;
  }, [steps]);

  // Ref to the currently-mounted StepForm, used by navigateSafe to flush
  // any pending draft to IndexedDB before navigation changes the step key.
  // Declared before the early returns so the hook count is stable.
  const stepFormRef = useRef<StepFormRef>(null);

  // Flush-before-navigate: captures the flush method synchronously (before
  // state updates cause the StepForm to unmount), awaits the IDB write, then
  // runs the navigation. Best-effort — a flush failure never blocks navigation.
  const navigateSafe = useCallback(async (fn: () => void): Promise<void> => {
    if (!isCurrentNavigation()) return;
    const flushFn = stepFormRef.current?.flush;
    if (flushFn) {
      try { await flushFn(); } catch { /* navigation always proceeds */ }
    }
    if (isCurrentNavigation()) fn();
  }, [isCurrentNavigation]);

  if (loading || stepsState.loading) return <div className="card skeleton">Loading your application…</div>;
  if (error || stepsState.error) return <div className="card" role="alert"><p>{error ?? stepsState.error}</p></div>;
  if (!active) return <div className="card"><h1>My application</h1><p>Start an application from your dashboard first.</p></div>;
  if (steps.length === 0) return <div className="card"><h1>My application</h1><p>Your workflow is being prepared — check back shortly.</p></div>;

  // Hub-aware progress: progress() excludes hub_step rows (structural containers)
  // so the denominator matches Globus's own percentage (Asana 1216802150410999 AC5).
  const { pct: progressPct } = progress(steps);
  const currentIdx = step ? phase[1].steps.findIndex((s) => s.stepKey === step.stepKey) : 0;
  // Cross-step contract: the engine's read_only_profile widget (About Me's
  // "Your Profile" card) renders the PERSONAL step's data — name, phone,
  // email and the photo (a data URL after the BFF's serve-side rehydration).
  // Without this prop it falls back to the current step's own data and
  // renders an empty circle.
  const personalStep = steps.find((s) => s.stepKey.startsWith('personal_information'));
  // Freshness token for the photo: saving Personal Information replaces the
  // account photo, then reload() delivers the NEW data URL — but the engine
  // seeds its cross-step widgets at mount, so an already-open next step (About
  // Me) kept showing the old image until the applicant navigated away and
  // back. Keying StepForm on the photo value forces that one remount exactly
  // when the photo changes; drafts (IndexedDB autosave) survive it.
  const photoVal = personalStep?.stepData?.profilePicture;
  const photoRev = typeof photoVal === 'string' ? `${photoVal.length}:${photoVal.slice(-12)}` : 'none';
  // Same mount-once problem as the photo, for the other read_only_profile
  // fields (Asana A08): saving Personal Information updates phone/name/email
  // in personalStep.stepData, but an already-open About Me never re-seeds
  // without its own remount. Fold the fields the widget actually displays
  // into a second freshness token alongside photoRev.
  const profileRev = computeProfileRev(personalStep?.stepData);
  // Next step in DISPLAY order (phases as shown, steps within each phase) —
  // where Save & Continue lands. NOT the served flat order: Globus serves
  // e.g. terms_conditions last in the array even though it renders in phase 1.
  const ordered = phases.flatMap(([, p]) => p.steps);
  const flatIdx = step ? ordered.findIndex((s) => s.stepKey === step.stepKey) : -1;
  const nextStep = flatIdx >= 0 ? ordered[flatIdx + 1] : undefined;
  // Footer Back (Asana 1216563978715930): plain navigation in display order,
  // same freedom as the stepper (which gates nothing), crossing phase
  // boundaries. The engine flushes the pending autosave before invoking it.
  // The standalone footer "Next" was removed (Asana 1217761042299537) —
  // forward progression is Save & Continue only.
  const prevStep = flatIdx > 0 ? ordered[flatIdx - 1] : undefined;
  // Select the section that actually CONTAINS the target step. Never
  // `target.phaseKey` — that is only the section id in the legacy grouping mode;
  // under bucketing it matches no tab and would strand navigation on tab 1.
  // goTo flushes any pending IDB draft before switching the active step so
  // photo data-URLs survive Back/Next, stepper, and mobile navigation without
  // requiring a submit. The flush is a no-op when submittingRef is true (the
  // step was just saved) or when nothing was tracked yet.
  const goTo = (target?: NormalStep) => {
    if (!target) return;
    // Reset scroll position on every Back/Next/Save & Continue advance: the
    // stepper's own scrollIntoView (below) only ever moves the horizontal
    // step-circle row, never the page itself. Without this, saving from the
    // bottom of a long step leaves the viewport at that same absolute scroll
    // offset — which lands on/near the bottom of a shorter next step instead
    // of its title.
    window.scrollTo({ top: 0, behavior: 'auto' });
    void navigateSafe(() => {
      setPhaseKey(sectionKeyOf(phases, target) ?? target.phaseKey);
      setStepKey(target.stepKey);
    });
  };

  return (
    <>
      {isMobile ? (
        <MobileApplicationNav
          phases={phases}
          activePhaseKey={phase[0]}
          steps={steps}
          currentStep={step}
          programName={active.programName}
          onSelectPhase={(k) => { void navigateSafe(() => { setPhaseKey(k); setStepKey(undefined); }); }}
          onSelectStep={(k) => { void navigateSafe(() => setStepKey(k)); }}
        />
      ) : (
        <ApplicationStepperNav
          phases={phases}
          phase={phase}
          step={step}
          steps={steps}
          progressPct={progressPct}
          onSelectPhase={(k) => { void navigateSafe(() => { setPhaseKey(k); setStepKey(undefined); }); }}
          onSelectStep={(k) => { void navigateSafe(() => setStepKey(k)); }}
        />
      )}

      {/* Keep the applicant-facing rejection surface outside the form branch so
          reference and temporarily-hydrating steps receive the same contract. */}
      {step?.needsAction &&
        submittedRejections[step.stepKey] !== rejectionFingerprint(step) &&
        step.rejectionMessage && (
          <div className="globus-banner review-feedback-banner" role="alert">
            <strong>Changes requested</strong>
            <div>{step.rejectionMessage}</div>
          </div>
        )}
      {step?.needsAction &&
        submittedRejections[step.stepKey] === rejectionFingerprint(step) && (
          <div className="globus-banner ok" role="status">
            <strong>Submitted for review</strong>
          </div>
        )}

      {step && referenceStep && (
        // Reference steps are request-row management, not a form — the cards
        // own their own state machine and the step auto-completes in Globus
        // once enough references are approved. The Save & Continue footer is
        // reassurance + navigation only (everything already saved on Send
        // Request); it advances exactly like the engine's button.
        <ReferenceDetailsStep
          key={`${active.id}:${step.stepKey}`}
          appId={active.id}
          isLast={!nextStep}
          readOnly={isReadOnly(step)}
          canSubmit={canSubmitStep(step)}
          onBack={prevStep ? () => goTo(prevStep) : undefined}
          onContinue={async () => {
            await refreshStep(step.stepKey);
            if (isCurrentNavigation()) goTo(nextStep);
          }}
        />
      )}
      {/* Agreement step awaiting (or failing) hydration — renderStep is
          undefined by the hook's contract, so the placeholder never renders. */}
      {step && !referenceStep && !renderStep && (
        hydrationError ? (
          <div className="card formcard">
            <div className="globus-banner err" role="alert">
              {AGREEMENT_UNAVAILABLE_NOTICE}
              {shortTechnicalCode(hydrationError) ? ` (${shortTechnicalCode(hydrationError)})` : ''}
            </div>
            <p className="formactions">
              <button type="button" style={{ width: 'auto' }} onClick={reloadHydratedStep} data-testid="button-agreement-retry">
                Try again
              </button>
            </p>
          </div>
        ) : (
          <div className="card formcard skeleton">Loading step…</div>
        )
      )}
      {step && !referenceStep && renderStep && (
        <StepForm
          ref={stepFormRef}
          key={`${active.id}:${step.stepKey}:${photoRev}:${profileRev}`}
          appId={active.id}
          programCode={active.programCode}
          step={renderStep}
          position={{ n: currentIdx + 1, of: phase[1].steps.length }}
          isLast={!nextStep}
          // Hub step wiring (Asana 1216802150410999): build HubChecklistData
          // from the hub step's sub-task map. The engine renders HubOverview
          // instead of auto-skipping when this prop is present on a hub step.
          // onOpenSubTask navigates to the sub-task step within the same phase.
          {...(isHubStep(renderStep) && renderStep.hubSubtaskMap?.length
            ? {
                hubChecklist: {
                  title: renderStep.title,
                  description: renderStep.description,
                  subTasks: renderStep.hubSubtaskMap.map((key) => {
                    const sub = steps.find((s) => s.stepKey === key);
                    return {
                      key,
                      title: sub?.title ?? key,
                      // Engine contract (HubSubTaskEntry.status, v2.31.0): only
                      // 'completed' | 'in_progress' render distinctly — any other
                      // string falls back to Not Started. Flagged/attention steps
                      // therefore map to 'in_progress' so they don't render as
                      // untouched — even when the raw workflow status is
                      // completed (rejection / compliance feedback).
                      status: sub ? (isCompleteForApplicant(sub) ? 'completed' : (isDone(sub) || needsAttention(sub)) ? 'in_progress' : 'not_started') : 'not_started',
                    };
                  }),
                  onOpenSubTask: (subTaskKey: string) => {
                    // Sub-task steps are filtered OUT of `phases` (they never
                    // render as top-level stepper nodes), so the section is
                    // resolved from the HUB step — the node the applicant is
                    // on — and only the stepKey targets the sub-task. `step`
                    // resolution below falls back to `steps` for sub-task keys.
                    const sub = steps.find((s) => s.stepKey === subTaskKey);
                    if (sub && renderStep) {
                      const hubHost = steps.find((s) => s.stepKey === renderStep.stepKey) ?? sub;
                      void navigateSafe(() => {
                        setPhaseKey(sectionKeyOf(phases, hubHost) ?? hubHost.phaseKey);
                        setStepKey(sub.stepKey);
                      });
                    }
                  },
                },
              }
            : {})}
          onBack={prevStep ? () => goTo(prevStep) : undefined}
          personalStepData={personalStep?.stepData}
          personalInfoReadOnly={personalStep ? isReadOnly(personalStep) : false}
          expectedPassportName={expectedPassportName}
          approvedDocIds={approvedDocIds}
          /** Step-access contract props — driven exclusively by the server's
           *  step-access snapshot; never derived from workflow or journey data. */
          readOnly={isReadOnly(step)}
          canSubmit={canSubmitStep(step)}
          lockBannerMessage={step.lockBannerMessage}
          lockReason={step.lockReason}
          unlockConditions={step.unlockConditions}
          resolveStepTitle={resolveStepTitle}
          viewOnly={step.accessLevel === 'view_only'}
          unlockAt={step.unlockAt}
          suppressNeedsAction={
            Boolean(step.needsAction) &&
            submittedRejections[step.stepKey] === rejectionFingerprint(step)
          }
          onSubmitAccepted={() => {
            if (step?.needsAction) {
              setSubmittedRejections((current) => ({
                ...current,
                [step.stepKey]: rejectionFingerprint(step),
              }));
            }
          }}
          hideReviewBanner
          onAccessRefresh={refetchAccess}
          // Upload-triggered CBC submission (task: start processing on upload):
          // refresh the step/access data exactly like a normal submit, but
          // NEVER navigate — the applicant stays on the CBC step and watches
          // the processing state appear in place.
          onAutoSubmitted={async () => {
            await refreshStep(step.stepKey);
          }}
          onSubmitted={async () => {
            // Awaited inside save(): the engine's submit promise stays pending
            // until the step-access snapshot is fresh, so the subsequent
            // navigation can never land on a stale locked/view-only render.
            await refreshStep(step.stepKey);
            // Save & Continue CONTINUES: advance to the next step in served
            // order, crossing phase boundaries. The final step has no next —
            // the form stays put and the "Saved." banner is the feedback.
            if (isCurrentNavigation()) goTo(nextStep);
          }}
        />
      )}
    </>
  );
}

/**
 * The Globus "Showcase Yourself" step (showcase_yourself_v1_0) ships as discrete
 * fields — a `video_introduction` text field and a `photo_gallery` of type
 * `file_upload_advanced`. The engine renders that gallery with a plain
 * `<img src>` and uploads via its same-origin GLOBUS data-boundary default
 * (`POST /api/upload-photo`), neither of which works on this split-origin,
 * Bearer-auth portal: the upload hits the SPA fallback (no URL back) and a plain
 * <img> cannot send the Bearer, so thumbnails never render. We instead render
 * this step with our own ShowcaseYourself control (AuthImage fetches thumbnails
 * as authenticated blobs; uploads go through the BFF document endpoint), keeping
 * the Globus step_data shape unchanged (top-level video_introduction /
 * photo_gallery / photo_captions). Mirrors the ReferenceDetailsStep pattern for
 * a step the render-only engine cannot drive.
 */
const isShowcaseStep = (stepKey: string) => /showcase/i.test(stepKey);

/** Map top-level Globus step_data into the ShowcaseYourself value shape,
 *  dropping gallery entries with no resolvable URL (the empty refs the broken
 *  engine path persisted — they never rendered and must not crash refName). */
function seedShowcaseValue(data: Record<string, unknown> | undefined): ShowcaseValue {
  const raw = (data ?? {}) as Record<string, any>;
  const gallery = Array.isArray(raw.photo_gallery) ? raw.photo_gallery : [];
  const photo_gallery = gallery
    .map((p: any): PhotoRef | string | null => {
      if (typeof p === 'string' && p) return p;
      if (!p || typeof p !== 'object') return null;
      const url = p.url ?? p.downloadUrl ?? p.download_url;
      if (typeof url !== 'string' || !url) return null;
      // Preserve the per-photo control state (rotate/rename/dedup) and any host
      // moderation status so they survive a reload.
      return {
        url,
        filename: p.filename ?? p.fileName ?? p.originalName ?? p.original_name,
        displayName: typeof p.displayName === 'string' ? p.displayName : undefined,
        rotation: typeof p.rotation === 'number' ? p.rotation : undefined,
        hash: typeof p.hash === 'string' ? p.hash : undefined,
        approvalStatus: p.approvalStatus ?? p.approval_status,
        rejectionReason: p.rejectionReason ?? p.rejection_reason,
      };
    })
    .filter((p): p is PhotoRef | string => p !== null);
  return {
    video_introduction: typeof raw.video_introduction === 'string' ? raw.video_introduction : '',
    photo_gallery,
    photo_captions:
      raw.photo_captions && typeof raw.photo_captions === 'object' ? raw.photo_captions : {},
  };
}

/**
 * Fetch adapter for the engine's SWT eligibility endpoints. The engine's
 * default is a plain relative fetch with no auth header — on the deployed
 * split client/server Cloud Run setup that hits the static client origin
 * (index.html, "Network error") and would 401 even same-origin. Prefix the
 * BFF base URL and attach the session Bearer, same as lib/api. Stable
 * module-level fn; reads the token at call time.
 */
function eligibilityApiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  const token = auth.getToken();
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

/** Skills & Hobbies steps carry a composite the engine renders with an external
 *  sponsorReviewItems prop — detect by stepKey prefix (engine contract). */
const isSkillsHobbiesStep = (stepKey: string) => /skills_and_hobbies|skills_hobbies/i.test(stepKey);

/**
 * Strip any field value that is a data: URL from a form-data object before
 * sending it to the BFF saveDraft endpoint. The server already discards them
 * (applications.controller.ts guard), but stripping client-side avoids
 * shipping ~200KB of base64 that will be thrown away anyway (Option B cleanup).
 * Field-agnostic: any string value starting with "data:" is dropped.
 */
export function stripDataUrls(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (typeof v !== 'string' || !v.startsWith('data:')) out[k] = v;
  }
  return out;
}

/**
 * Freshness token for the read_only_profile fields (About Me's "Your
 * Profile" card): firstName/lastName/email/phone. Same mount-once problem as
 * photoRev (Asana A08) — folding these into the StepForm remount key forces
 * an already-open About Me to re-seed when Personal Information changes.
 */
export function computeProfileRev(personalStepData: Record<string, unknown> | undefined): string {
  if (!personalStepData) return 'none';
  return JSON.stringify({
    firstName: personalStepData.firstName,
    lastName: personalStepData.lastName,
    email: personalStepData.emailAddress ?? personalStepData.email,
    phone: personalStepData.telephone ?? personalStepData.phoneNumber ?? personalStepData.phone,
  });
}

/* Phase sections are produced by groupSteps — bucket ids under the 3-phase
 * contract, raw phaseKeys in the legacy fallback. Treat the key as opaque. */

const barTrack: CSSProperties = {
  width: '100%', height: 6, borderRadius: 999, overflow: 'hidden', backgroundColor: 'var(--line)',
};

function MobileApplicationNav({
  phases,
  activePhaseKey,
  steps,
  currentStep,
  programName,
  onSelectPhase,
  onSelectStep,
}: {
  phases: PhaseSection[];
  activePhaseKey: string;
  steps: NormalStep[];
  currentStep?: NormalStep;
  programName?: string;
  onSelectPhase: (key: string) => void;
  onSelectStep: (stepKey: string) => void;
}) {
  // Hub-aware: use progress() which excludes hub_step rows (AC5)
  const { pct: overallPct } = progress(steps);
  const phaseIndex = Math.max(0, phases.findIndex(([k]) => k === activePhaseKey));
  const phase = phases[phaseIndex]?.[1];
  const phaseSteps = phase?.steps ?? [];
  const currentIdx = currentStep ? phaseSteps.findIndex((s) => s.stepKey === currentStep.stepKey) : 0;
  const future = phases.slice(phaseIndex + 1);

  return (
    <div className="appnav">
      {/* Header */}
      <header style={{ marginBottom: 18 }}>
        {programName && (
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-2)', margin: '0 0 6px' }}>
            {programName}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.01em' }}>My application</h1>
          <span style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 10, fontSize: 13, fontWeight: 700, backgroundColor: 'var(--brand-50)', color: 'var(--brand-600)', border: '1px solid rgba(15, 76, 129, 0.1)' }}>
            {overallPct}% done
          </span>
        </div>

        {/* Segmented phase switcher */}
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          {phases.map(([k, p]) => {
            const active = k === activePhaseKey;
            const done = p.steps.filter((candidate) => isNavigationComplete(candidate, steps)).length;
            const pct = p.steps.length ? Math.round((done / p.steps.length) * 100) : 0;
            return (
              <button
                key={k}
                type="button"
                className="appnav-seg"
                onClick={() => onSelectPhase(k)}
                aria-current={active ? 'true' : undefined}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6, textAlign: 'left' }}
              >
                <span style={{ ...barTrack }}>
                  <span style={{ display: 'block', height: '100%', borderRadius: 999, width: `${pct}%`, backgroundColor: active ? 'var(--brand)' : 'var(--ok)', transition: 'width .5s ease' }} />
                </span>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: active ? 'var(--brand-600)' : 'var(--ink-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: active ? 'var(--brand)' : 'var(--ink-2)', flexShrink: 0 }}>{done}/{p.steps.length}</span>
                </span>
              </button>
            );
          })}
        </div>
      </header>

      {/* Step nav card */}
      <div style={{ position: 'relative', overflow: 'hidden', backgroundColor: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 18, boxShadow: 'var(--shadow)', marginBottom: 20 }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, display: 'flex', gap: 2, padding: '0 2px' }}>
          {phaseSteps.map((s, i) => {
            const railColor = needsNavigationAttention(s, steps)
              ? 'var(--err)'
              : isNavigationComplete(s, steps)
                ? 'var(--ok)'
                : i === currentIdx
                  ? 'var(--brand)'
                  : 'var(--line)';
            return (
              <span
                key={s.stepKey}
                style={{
                  flex: 1,
                  height: '100%',
                  borderRadius: '0 0 2px 2px',
                  backgroundColor: railColor,
                }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: '0 0 2px', fontSize: 12, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--brand)' }}>
              Step {currentIdx + 1} of {phaseSteps.length}
            </p>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentStep?.title}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              className="appnav-chip"
              aria-label="Previous step"
              disabled={currentIdx <= 0}
              onClick={() => currentIdx > 0 && onSelectStep(phaseSteps[currentIdx - 1].stepKey)}
              style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--line)', backgroundColor: '#fff', color: 'var(--ink)', cursor: currentIdx > 0 ? 'pointer' : 'not-allowed', opacity: currentIdx > 0 ? 1 : 0.4 }}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="appnav-chip"
              aria-label="Next step"
              disabled={currentIdx >= phaseSteps.length - 1}
              onClick={() => currentIdx < phaseSteps.length - 1 && onSelectStep(phaseSteps[currentIdx + 1].stepKey)}
              style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', backgroundColor: 'var(--brand)', color: '#fff', cursor: currentIdx < phaseSteps.length - 1 ? 'pointer' : 'not-allowed', opacity: currentIdx < phaseSteps.length - 1 ? 1 : 0.4 }}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Vertical step list for the active phase */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: '0 4px', marginBottom: 8 }}>
        {phaseSteps.map((s, i) => {
          const attention = needsNavigationAttention(s, steps);
          const stepDone = isNavigationComplete(s, steps);
          const isCurrent = s.stepKey === currentStep?.stepKey;
          const last = i === phaseSteps.length - 1;
          return (
            <button
              key={s.stepKey}
              type="button"
              className="appnav-step"
              onClick={() => onSelectStep(s.stepKey)}
              aria-current={isCurrent ? 'step' : undefined}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 16, border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', opacity: !stepDone && !isCurrent && !attention ? 0.7 : 1 }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}><AppStepDot done={stepDone} current={isCurrent} attention={attention} index={i} /></span>
              <span style={{ flex: 1, minWidth: 0, paddingBottom: last ? 8 : 20, borderBottom: last ? 'none' : '1px solid var(--line)' }}>
                <span style={{ display: 'block', fontSize: 16, fontWeight: isCurrent ? 700 : stepDone || attention ? 600 : 500, color: attention ? 'var(--err)' : isCurrent ? 'var(--brand-600)' : stepDone ? 'var(--ink)' : 'var(--ink-2)' }}>{s.title}</span>
                {isCurrent && <span style={{ display: 'block', fontSize: 14, fontWeight: 500, marginTop: 4, color: 'var(--brand)' }}>In progress</span>}
                {attention && !isCurrent && <span style={{ display: 'block', fontSize: 14, fontWeight: 500, marginTop: 4, color: 'var(--err)' }}>Needs attention</span>}
              </span>
            </button>
          );
        })}
      </div>

      {/* Future phases overview */}
      {future.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 20, borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {future.map(([k, p]) => {
            const done = p.steps.filter((candidate) => isNavigationComplete(candidate, steps)).length;
            return (
              <button
                key={k}
                type="button"
                className="appnav-phase"
                onClick={() => onSelectPhase(k)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderRadius: 12, border: '1px solid rgba(22, 33, 43, 0.05)', cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{p.name}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)' }}>{done}/{p.steps.length}</span>
                  <ChevronRight size={16} style={{ color: 'var(--ink-2)', flexShrink: 0 }} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppStepDot({ done, current, attention, index }: { done: boolean; current: boolean; attention: boolean; index: number }) {
  const base: CSSProperties = {
    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };
  if (attention) return <span style={{ ...base, backgroundColor: 'var(--err)', color: '#fff', fontWeight: 700 }}>!</span>;
  if (done) return <span style={{ ...base, backgroundColor: 'var(--ok)', color: '#fff' }}><Check size={16} strokeWidth={3} /></span>;
  if (current) {
    return (
      <span style={{ ...base, position: 'relative' }}>
        <span className="mj-ping" style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: 'var(--brand)', opacity: 0.2 }} />
        <span style={{ ...base, position: 'relative', border: '2.5px solid var(--brand)', backgroundColor: 'var(--card)', color: 'var(--brand)' }}><MapPin size={14} strokeWidth={2.5} /></span>
      </span>
    );
  }
  return <span style={{ ...base, border: '2px solid var(--line)', backgroundColor: 'var(--card)', color: 'var(--ink-2)', fontSize: 12, fontWeight: 700 }}>{index + 1}</span>;
}

/**
 * Imperative handle exposed by StepForm via forwardRef. The parent
 * (ApplicationPage) calls flush() before any non-submit navigation to
 * guarantee the current engine form state — including photo data URLs — is
 * written to IndexedDB before the StepForm unmounts.
 */
type StepFormRef = { flush: () => Promise<void> };

/** How long `save()` waits before retrying a step-submit that classifySaveError
 *  scored as a true network/timeout failure (Asana 1217982476199692). Exported
 *  so tests can advance fake timers by exactly this amount instead of a magic
 *  literal. */
export const NETWORK_RETRY_DELAY_MS = 1500;
const CAMP_DURATION_REASON = 'CAMP_PROGRAM_DURATION_EXCEEDS_FOUR_MONTHS';
const CAMP_DURATION_APPLICANT_MESSAGE =
  'Your Camp Contract dates cover more than four calendar months. Open the Camp Contract step and correct the Contract Start Date and Contract End Date so the period is no more than four calendar months. If the dates on your signed contract changed, upload a corrected signed contract. Save and resubmit the step for review. Ready for SEVIS remains blocked until our team verifies the corrected contract and updates the official placement dates.';
const CAMP_CONTRACT_CORRECTION_FIELDS = [
  'contract_start_date',
  'contract_end_date',
  'camp_contract_file',
] as const;

const campContractReadOnlyFields = (
  fields: string[],
  hasDurationFeedback: boolean,
  editableIdentifiers: Set<string>,
) =>
  hasDurationFeedback
    ? fields.filter((fieldKey) => !editableIdentifiers.has(fieldKey))
    : fields;

function campContractFieldIdentifiers(fieldsConfig: unknown, target: string): string[] {
  const identifiers = new Set([target]);
  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== 'object') return;
    const row = value as Record<string, unknown>;
    const name = typeof row.name === 'string' ? row.name : undefined;
    const key = typeof row.key === 'string' ? row.key : undefined;
    if (name === target || key === target) {
      if (name) identifiers.add(name);
      if (key) identifiers.add(key);
    }
    Object.values(row).forEach(visit);
  };
  visit(fieldsConfig);
  return [...identifiers];
}

export function rejectionFingerprint(step: Pick<NormalStep, 'rejectionCode' | 'rejectionMessage'>): string {
  return `${step.rejectionCode ?? ''}\u0000${step.rejectionMessage ?? ''}`;
}

export const StepForm = forwardRef<StepFormRef, {
  appId: string | number;
  /** Human-readable program code (e.g. "CAMP_WP") — forwarded to the engine's
   *  programId prop for brand/program-gated step copy. Distinct from the
   *  Globus program UUID, which this component never needs. */
  programCode?: string;
  step: NormalStep;
  position: { n: number; of: number };
  /** True on the final step of the whole application — flips the engine's
   *  button label from "Save & Continue" to "Save". */
  isLast: boolean;
  /** Hub checklist page data (engine contract HubChecklistData, v2.31.0).
   *  Forwarded verbatim to FormEnginePortable — the engine renders its
   *  HubOverview instead of a form when this is present AND the served
   *  stepConfig.stepType is 'hub'. Only ever passed for hub steps. */
  hubChecklist?: {
    title?: string;
    description?: string;
    subTasks: Array<{ key: string; title: string; status?: string }>;
    onOpenSubTask: (subTaskKey: string) => void;
  };
  /** Footer Back navigation without validation/completion — the engine
   *  flushes the pending autosave first. Undefined at the first step.
   *  Forward progression is Save & Continue only (Asana 1217761042299537). */
  onBack?: () => void;
  /** The personal-information step's saved data — feeds the engine's
   *  cross-step widgets (read_only_profile photo/name/contact). */
  personalStepData?: Record<string, unknown>;
  /** Compatibility prop for existing callers. Passport corrections no longer
   * use a disagreement-only gate against Personal Information. */
  personalInfoReadOnly?: boolean;
  expectedPassportName?: { givenNames?: string; surname?: string };
  /** Globus document ids that have passed review (approved/official). An upload
   *  field whose persisted value references one of these is locked read-only. */
  approvedDocIds?: Set<string>;
  /** True when the server's step-access contract marks this step `locked` or
   *  `view_only`. All inputs are disabled; Save & Continue is hidden. Driven
   *  exclusively by the step-access snapshot — never derived locally. */
  readOnly?: boolean;
  /** Authoritative submit capability. May be false while editing remains open. */
  canSubmit?: boolean;
  /** Staff-authored applicant-facing banner text. Rendered verbatim when the
   *  step is locked and the lock carries an authored message. */
  lockBannerMessage?: string;
  /** Machine-readable lock reason code from the step-access snapshot — used
   *  only to pick fallback banner copy when no authored message exists. */
  lockReason?: string;
  unlockConditions?: unknown;
  /** Maps a prerequisite step key named in `unlockConditions` (e.g. the fact
   *  `step.personal_information.status`) to its configured applicant-facing
   *  title, so UPSTREAM_STEP_INCOMPLETE copy names the step, not the key. */
  resolveStepTitle?: (stepKey: string) => string | undefined;
  /** True when the access level is `view_only` (vs `locked`) — picks the
   *  softer fallback banner copy. */
  viewOnly?: boolean;
  /** ISO UTC timestamp of the scheduled auto-release for time-window locks.
   *  Displayed in the applicant's local timezone; never used to compute
   *  whether the lock has expired. */
  unlockAt?: string;
  /** True after this exact rejection was successfully resubmitted locally,
   * while Globus still returns the stale needs_action projection. */
  suppressNeedsAction?: boolean;
  /** Called immediately after Globus accepts the submit, before snapshot
   * refresh/navigation, to establish optimistic awaiting-review state. */
  onSubmitAccepted?: () => void;
  /** ApplicationPage owns the universal banner above all step render branches. */
  hideReviewBanner?: boolean;
  /** Callback to re-fetch the step-access snapshot — called when the server
   *  rejects a save with STEP_LOCKED so the UI reflects the new lock state
   *  without a full page reload. */
  onAccessRefresh?: () => void;
  /** Compatibility callback only; extraction never invokes canonical name sync. */
  onPassportNameSynced?: () => void | Promise<void>;
  /** Called after a successful submit. Awaited by save(): the returned promise
   *  must resolve only once the step-access snapshot is fresh, so the
   *  post-submit navigation never renders from stale lock state. */
  onSubmitted: () => void | Promise<void>;
  /** Called after a successful UPLOAD-TRIGGERED submit (CBC auto-submit).
   *  Refreshes step/access data like onSubmitted but must never navigate —
   *  the applicant stays on the step and watches the processing state. */
  onAutoSubmitted?: () => void | Promise<void>;
}>(function StepForm({
  appId,
  programCode,
  step,
  position,
  isLast,
  hubChecklist,
  onBack,
  personalStepData,
  expectedPassportName,
  approvedDocIds,
  readOnly,
  canSubmit = !readOnly,
  lockBannerMessage,
  lockReason,
  unlockConditions,
  resolveStepTitle,
  viewOnly,
  unlockAt,
  suppressNeedsAction,
  onSubmitAccepted,
  hideReviewBanner,
  onAccessRefresh,
  onSubmitted,
  onAutoSubmitted,
}, ref) {
  // Keep the module-level upload target in sync with the application this
  // step belongs to, so engine document uploads carry X-Application-Id.
  useEffect(() => {
    engineUploadApplicationId = String(appId);
    return () => {
      // Reset on unmount so a stale id can never outlive its form; the BFF
      // then falls back to its (loud-failure) server-side resolution.
      if (engineUploadApplicationId === String(appId)) engineUploadApplicationId = null;
    };
  }, [appId]);
  // Preview resolver for engine file fields. The Hub rewrites uploaded-file
  // references in persisted step_data to its own portal-relative shape
  // (/api/documents/:docId/download) — a path this BFF does not serve, so the
  // raw fetch fell through Vite's proxy to the SPA and "previewed" index.html
  // as a blank image. Rewrite that shape onto the BFF's application-scoped
  // download route before fetching.
  const resolveStepDocumentUrl = useCallback(
    (path: string) => {
      const mediaId = stepMediaIdFromUrl(path);
      if (mediaId) return fetchDocumentObject(`/api/v1/step-media/${mediaId}/file`);
      const hubShaped = /^\/api\/documents\/([0-9a-f-]{36})\/download$/i.exec(path);
      const resolved = hubShaped
        ? `/api/v1/applications/${appId}/documents/${hubShaped[1]}/download`
        : path;
      return fetchDocumentObject(resolved);
    },
    [appId],
  );
  const [initial, setInitial] = useState<Record<string, unknown>>();
  const [registry, setRegistry] = useState<unknown>();
  const [notice, setNotice] = useState<string>();
  const [errorNotice, setErrorNotice] = useState<string>();
  const [fieldValidationNotice, setFieldValidationNotice] = useState(false);
  const [submitErrorAttempt, setSubmitErrorAttempt] = useState(0);
  const showPageError = (message: string | undefined) => {
    setFieldValidationNotice(false);
    setErrorNotice(message);
  };
  const showSubmitError = (message: string) => {
    showPageError(message);
    // An applicant may retry the same failed save from the bottom of a long
    // form. The message has not changed, but it must come back into view.
    setSubmitErrorAttempt((attempt) => attempt + 1);
  };
  const showFieldValidationNotice = (message: string) => {
    // The engine renders the field error inline and scrolls to it. Keep the
    // summary announced, but do not pull the applicant back to this banner.
    setFieldValidationNotice(true);
    setErrorNotice(message);
  };
  const [warning, setWarning] = useState<string>();
  useEffect(() => {
    setNotice(undefined);
    showPageError(undefined);
    setWarning(undefined);
  }, [appId, step.stepKey]);
  // Skills & Hobbies feedback rides the already-owned step-access snapshot.
  // Prefer review_items because it retains targetLabel/fieldPath. Matching in
  // the engine remains on targetItemId; fieldPath array indexes are metadata,
  // never identity.
  const reviewItems = useMemo<ReviewFeedbackItem[]>(() => {
    if (step.reviewItems !== undefined) {
      return step.reviewItems.map((item) => ({
          id: item.id,
          targetItemId: item.targetItemId,
          targetType: item.targetType,
          fieldKey: item.fieldPath,
          targetLabel: item.targetLabel,
          fieldPath: item.fieldPath,
          status: item.status,
          comment: item.comment,
          createdAt: item.requestedAt,
        }));
    }
    // Legacy step-access projections omitted reviewItems entirely. Only those
    // snapshots may fall back to reviewFeedback; an authoritative [] means the
    // staff team cleared every item and stale feedback must stay gone.
    return step.reviewFeedback?.items ?? [];
  }, [step.reviewItems, step.reviewFeedback]);
  const errorBannerRef = useRef<HTMLDivElement>(null);
  const campDurationFeedback = step.complianceFeedback?.find(
    (feedback) => feedback.reasonCode === CAMP_DURATION_REASON,
  );
  const campCorrectionIdentifiers = useMemo(() => {
    const fieldsConfig = (step.stepConfig as { fieldsConfig?: unknown } | undefined)?.fieldsConfig;
    return new Set(
      CAMP_CONTRACT_CORRECTION_FIELDS.flatMap((target) =>
        campContractFieldIdentifiers(fieldsConfig, target),
      ),
    );
  }, [step.stepConfig]);
  // The engine has no CSS-var/data-theme contract of its own and never reads
  // the host <html> — it only reacts to this explicit prop.
  const scheme = useResolvedScheme();
  const showcase = isShowcaseStep(step.stepKey);
  // Showcase step is driven by our own control (see isShowcaseStep). Local value
  // is seeded from the merged step data once it loads; onChange merges partials.
  const [scValue, setScValue] = useState<ShowcaseValue>({});
  const [scValid, setScValid] = useState(false);
  const [scShowErrors, setScShowErrors] = useState(false);
  // Showcase server-draft autosave. The showcase step bypasses the engine
  // (custom control), so it carries its own 3s-debounced Globus draft save +
  // indicator, mirroring the engine's cadence. IndexedDB writes stay in
  // onChange (instant); this is the durable half. `scDirty` skips the initial
  // hydration seed; the seq ref discards out-of-order resolutions.
  const [scSaveStatus, setScSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // Classified message for the most recent autosave failure (Asana
  // 1217767958880539) — undefined for a true network failure, in which case
  // the status line below keeps its own autosave-specific copy rather than
  // classifySaveError's generic one.
  const [scSaveErrorMessage, setScSaveErrorMessage] = useState<string>();
  const scSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scSaveSeq = useRef(0);
  const scDirty = useRef(false);
  const { ctx, reload: reloadPortalContext } = usePortalContext();
  const { provenance, tracksProvenance, draftMetaFor, createFactory: createPassportFactory, isCurrent: isCurrentForm, onDocumentRemoved: onPassportDocumentRemoved, onFieldChange: onPassportFieldChange, reconcileFormValues } =
    usePassportCapture({
      appId,
      stepKey: step.stepKey,
      fieldsConfig: (step.stepConfig as { fieldsConfig?: unknown } | undefined)?.fieldsConfig,
      readOnly,
      onErrorNotice: showPageError,
    });
  // Identity notice surface: Personal Information and the applicant's own
  // passport step (dependants' passports never touch canonical identity).
  const identityStepKind: 'personal' | 'passport' | undefined = step.stepKey.startsWith('personal_information')
    ? 'personal'
    : tracksProvenance && !/child|spouse|dependant/.test(step.stepKey)
      ? 'passport'
      : undefined;
  const identityAnnotations = useIdentityAnnotations(appId, identityStepKind !== undefined);
  // Submit/autosave race orchestrator (task #14). See lib/stepSaveOrchestrator.ts
  // for the full invariant documentation. Recreated when appId or stepKey
  // changes — StepForm already remounts on those changes via its key prop, so
  // this memo always produces a fresh orchestrator for a fresh step.
  // stripDataUrls is applied to the server-draft path (Option B cleanup): the
  // BFF guard already discards data: URLs, but filtering here avoids shipping
  // ~200KB base64 payloads over the wire. The IDB path keeps the full value so
  // photo data URLs survive navigation (the whole point of task #15).
  const orchestrator = useMemo(
    () =>
      createSaveOrchestrator({
        saveDraft: (data) => isCurrentForm() ? portal.saveDraft(appId, step.stepKey, stripDataUrls(data)) : Promise.resolve(),
        saveLocalDraft: (data) => isCurrentForm() ? drafts.save(appId, step.stepKey, data, draftMetaFor(data)).then(() => {}) : Promise.resolve(),
        clearLocalDraft: () => isCurrentForm() ? drafts.clear(appId, step.stepKey).then(() => {}) : Promise.resolve(),
      }),
    [appId, step.stepKey, draftMetaFor, isCurrentForm],
  );
  const scServerSave = useCallback(
    (value: ShowcaseValue) => {
      // Gate is checked inside orchestrator.scServerSave; UI state must not
      // update (no 'saving' flash) when the call is dropped.
      const seq = ++scSaveSeq.current;
      const p = orchestrator.scServerSave(value as Record<string, unknown>);
      if (!p) return;
      setScSaveStatus('saving');
      setScSaveErrorMessage(undefined);
      p.then(
        () => { if (scSaveSeq.current === seq) setScSaveStatus('saved'); },
        (e) => {
          if (scSaveSeq.current !== seq) return;
          // network: keep undefined so the status line below falls back to its
          // own autosave-specific "will retry saving as you type" copy instead
          // of classifySaveError's generic manual-save framing.
          const cls = classifySaveError(e);
          setScSaveErrorMessage(cls.code === 'network' ? undefined : cls.message);
          setScSaveStatus('error');
        },
      );
    },
    [orchestrator],
  );
  // Track the latest form values reported by the engine's customOnChange so
  // the flush function can write them to IDB without waiting for the autosave
  // debounce. Not used for showcase steps — those write to IDB on every
  // onChange already.
  const latestFormValuesRef = useRef<Record<string, unknown>>({});
  // Expose flush() to the parent (ApplicationPage) via the forwarded ref.
  // Called before any non-submit navigation (stepper, phase tabs, mobile nav)
  // to guarantee the current form state is in IDB before StepForm unmounts.
  // Best-effort direct IDB write — bypasses the orchestrator intentionally
  // (the orchestrator's server-draft path is not needed here; only the local
  // write matters for photo survival). A submitting/accepted save owns its
  // snapshot; navigation must not recreate its just-cleared local draft.
  useImperativeHandle(ref, () => ({
    flush: async () => {
      if (!isCurrentForm() || orchestrator.submittingRef.current) return;
      const data = latestFormValuesRef.current;
      if (Object.keys(data).length === 0) return;
      await drafts.save(appId, step.stepKey, data, draftMetaFor(data));
    },
  }), [appId, step.stepKey, draftMetaFor, isCurrentForm, orchestrator]);
  useEffect(() => {
    if (!showcase || isDone(step) || !scDirty.current) return;
    if (scSaveTimer.current) clearTimeout(scSaveTimer.current);
    scSaveTimer.current = setTimeout(() => {
      scSaveTimer.current = null;
      scServerSave(scValue);
    }, 3000);
    return () => {
      if (scSaveTimer.current) clearTimeout(scSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scValue]);
  /** Back for the showcase footer: flush the pending draft save, then navigate. */
  const scFlushThenNavigate = (navigate: () => void) => () => {
    if (scSaveTimer.current) {
      clearTimeout(scSaveTimer.current);
      scSaveTimer.current = null;
      scServerSave(scValue);
    }
    navigate();
  };
  // ── CBC upload lifecycle ───────────────────────────────────────────────────
  const stepFormMountedRef = useRef(true);
  useEffect(() => {
    stepFormMountedRef.current = true;
    return () => { stepFormMountedRef.current = false; };
  }, []);
  // Ref updated each render so the hook always calls the current save closure.
  const cbcAutoSubmitRef = useRef<(values: Record<string, unknown>) => Promise<unknown>>(
    () => Promise.resolve(),
  );
  const cbc = useCbcStatus(appId);
  const onCbcAutoSubmitError = useCallback(
    (notice: string) => { if (stepFormMountedRef.current) showPageError(notice); },
    [],
  );
  const onCbcPersistError = useCallback(
    (notice: string) => { if (stepFormMountedRef.current) showPageError(notice); },
    [],
  );
  const cbcAutoSubmitDelegate = useCallback(
    (values: Record<string, unknown>) => cbcAutoSubmitRef.current(values),
    [],
  );
  const {
    uploadLocks: cbcUploadLocks,
    processingDelays: cbcProcessingDelays,
    hubLang,
    hydrate: cbcHydrate,
    onFormChange: onCbcFieldChange,
    onSubmissionSucceeded: cbcOnSubmitSuccess,
  } = useCbcUploadLifecycle({
    appId,
    step,
    cbcStatus: cbc,
    autoSubmit: cbcAutoSubmitDelegate,
    latestFormValuesRef,
    readOnly,
    onAutoSubmitError: onCbcAutoSubmitError,
    onPersistError: onCbcPersistError,
    mountedRef: stepFormMountedRef,
  });
  // SWT status consumers (Hub Tasks #11/#12): enrollment-proof pipeline +
  // professor/university verification. Fetched only on the Proof of Student
  // Status step; errors render nothing (the step must stay usable when the
  // status surface is down). Display-only — no eligibility computed here.
  const isSwtProofStep = isProofOfStudentStatusStep(step.stepKey);
  const swtProof = useSwtEnrollmentProofStatus(appId, isSwtProofStep);
  const swtAcademic = useSwtAcademicVerificationStatus(appId, isSwtProofStep);
  // Lock (read-only) any upload field whose persisted value references a
  // document that has passed review. The engine hides Remove + the upload area
  // for read-only fields, so an approved doc can be viewed but not removed or
  // replaced (matches the "if they've passed the gate they can't remove it"
  // rule). Match by Globus document id (from the reference or its downloadUrl).
  const readOnlyFields = useMemo(() => {
    // CBC processing lock (Asana 1216953751804758): while the Hub pipeline is
    // processing a submitted background-check package, its upload field is
    // read-only (the engine hides Remove + the upload area). The lock releases
    // on a terminal status, the configured processing timeout, or a staff
    // replacement request — all derived in deriveCbcUploadLocks.
    const cbcLocked = cbcUploadLocks.map((l) => l.fieldKey);
    if (!approvedDocIds || approvedDocIds.size === 0 || !initial) {
      return campContractReadOnlyFields(
        cbcLocked,
        Boolean(campDurationFeedback),
        campCorrectionIdentifiers,
      );
    }
    const docIdOf = (v: unknown): string | undefined => {
      if (v == null) return undefined;
      const fromUrl = (u: unknown) => {
        if (typeof u !== 'string') return undefined;
        const m = u.match(/\/documents\/([^/]+)\/download/);
        return m ? m[1] : undefined;
      };
      if (typeof v === 'string') return fromUrl(v);
      if (typeof v === 'object') {
        const o = v as Record<string, unknown>;
        if (typeof o.documentId === 'string') return o.documentId;
        if (typeof o.id === 'string') return o.id;
        return fromUrl(o.downloadUrl) ?? fromUrl(o.url);
      }
      return undefined;
    };
    const out: string[] = [];
    for (const [key, val] of Object.entries(initial)) {
      const vals = Array.isArray(val) ? val : [val];
      if (vals.some((v) => { const id = docIdOf(v); return id != null && approvedDocIds.has(id); })) {
        out.push(key);
      }
    }
    // Fold in CBC fields locked while the Hub pipeline is processing.
    // Passport OCR values deliberately remain editable when this step itself
    // is editable; accepted-submission and document-review locks are still
    // enforced by the server-derived step/document permissions above.
    return campContractReadOnlyFields(
      Array.from(new Set([...out, ...cbcLocked])),
      Boolean(campDurationFeedback),
      campCorrectionIdentifiers,
    );
  }, [
    approvedDocIds,
    initial,
    cbcUploadLocks,
    campDurationFeedback,
    campCorrectionIdentifiers,
  ]);
  // Save and other page-level failures belong here. For field validation, the
  // engine owns the inline message and first-error scroll: focusing this
  // summary too would make a long form bounce between two distant targets.
  useLayoutEffect(() => {
    if (!errorNotice || fieldValidationNotice) return;
    const banner = errorBannerRef.current;
    banner?.focus({ preventScroll: true });
    banner?.scrollIntoView({ behavior: 'auto', block: 'center' });
  }, [errorNotice, fieldValidationNotice, submitErrorAttempt]);
  const cbcReviewNotice = Object.values(hubLang.reviewRequiredByField)[0];
  const cbcTranslationFallback = Object.values(hubLang.translationFallbackByField)[0];
  const onCbcTranslationUploaded = useCallback(() => {
    void cbc.refetchPackages();
  }, [cbc]);
  const lang = useLanguageSupport(step.stepKey, hubLang);
  const [apProvider, setApProvider] = useState<{
    Provider: React.ComponentType<{ value: unknown; children: React.ReactNode }>;
    defaultContext: Record<string, unknown>;
  }>();
  const stepProgress = { status: step.status, stepData: step.stepData ?? {}, step_data: step.stepData ?? {} };
  // Signature fields ("Full Legal Name") must match the applicant's legal
  // name — the engine (>=2.18) blocks submission on mismatch when given the
  // expected name from the resolved portal context.
  const expectedLegalName =
    [ctx?.firstName, ctx?.lastName].filter(Boolean).join(' ') || undefined;

  // Embassy/consulate option binding (VISA information step): the engine's
  // `embassy_city` searchable field renders whatever options the consumer
  // binds — it never fetches them — and auto-fills `embassy_country` from the
  // selected option's `country`. Options come from Globus via the BFF.
  const hasEmbassyField = useMemo(
    // Hub now accepts the M2M key (portal.read scope) on embassy-consulates
    // — verified live against dev Globus, so the fetch is enabled.
    () => JSON.stringify(step.stepConfig ?? '').includes('embassy_city'),
    [step.stepConfig],
  );
  const [embassyConfig, setEmbassyConfig] = useState<unknown>();
  useEffect(() => {
    if (!hasEmbassyField) { setEmbassyConfig(undefined); return; }
    let cancelled = false;
    portal.embassies().then(
      (records) => {
        if (cancelled) return;
        setEmbassyConfig(injectEmbassyOptions(step.stepConfig, toEmbassyOptions(records ?? [])));
      },
      (e) => {
        if (cancelled) return;
        setWarning('Embassy and consulate options could not be loaded. Please reload the page or try again later.');
        track('step_error', { stepKey: step.stepKey, ctx: 'embassy_options', message: e?.message });
      },
    );
    return () => { cancelled = true; };
  }, [hasEmbassyField, step.stepConfig, step.stepKey]);

  // Hydration is tied to saved step data/config, not account-name refreshes.
  // Long-lived registry callbacks read the latest context without remounting
  // the engine or resetting an in-flight extraction on every context render.
  const hydrationInputs = useRef({ step, cbcHydrate });
  hydrationInputs.current = { step, cbcHydrate };
  useEffect(() => {
    let cancelled = false;
    const { step: hydrationStep, cbcHydrate: hydrateCbc } = hydrationInputs.current;
    setInitial(undefined);
    latestFormValuesRef.current = {};
    // Draft merge, seeded through the engine's own initial-value logic so
    // defaults/conditionals resolve like v1 (README wiring). Order matters:
    // engine seed (field defaults + saved values) < IndexedDB draft < live
    // Globus step data — the draft must beat the seed's empty-string defaults
    // for unsaved fields, but Globus wins for anything it has actually saved.
    provenance.reset();
    const onHydrationFailure = () => {
      if (cancelled || !stepFormMountedRef.current) return;
      track('step_error', { stepKey: step.stepKey, ctx: 'step_hydration' });
      showPageError('Your saved step could not be loaded. Please reload before editing or submitting.');
    };
    Promise.all([
      drafts.load(appId, step.stepKey),
      engineHelpers(),
      tracksProvenance ? drafts.loadMeta(appId, step.stepKey) : undefined,
    ]).then(async ([draft, helpers, draftMeta]) => {
      // This local receipt records capture history, not server audit evidence.
      // Never use it to relabel an existing legacy draft, even if its values
      // happen to match a prior accepted submission. hydrate verifies the hash.
      const captureMeta = tracksProvenance && draft == null
        ? await drafts.loadAcceptedMeta(appId, step.stepKey)
        : draftMeta;
      if (cancelled || !stepFormMountedRef.current) return;
      if (helpers.FormEngineAPProvider) {
        setApProvider({ Provider: helpers.FormEngineAPProvider, defaultContext: helpers.formEngineDefaultContext });
      }
      // Register the BFF-backed passport extraction adapter. OCR-prefilled
      // fields stay editable while the server-derived step access allows it;
      // the provenance guard protects approved manual corrections from later
      // OCR responses without reintroducing the old client-side read-only rule.
      const reg: any = helpers.createEmptyRegistry?.() ?? {};
      if (helpers.applyPassportExtraction) {
        reg.passportExtractionFactory = createPassportFactory(helpers);
      }
      // Do not add a disagreement-only passport gate against the historical
      // Personal Information declaration. In a permitted correction state a
      // valid manual correction must reach the authenticated final-save path;
      // canonical promotion/validation belongs to the Hub, not this renderer.
      setRegistry(reg);
      const config = (hydrationStep.stepConfig as any)?.fieldsConfig ?? null;
      const hydrationProgress = { status: hydrationStep.status, stepData: hydrationStep.stepData ?? {}, step_data: hydrationStep.stepData ?? {} };
      const engineSeed = helpers.getInitialFormValues
        ? helpers.getInitialFormValues(config, hydrationProgress, {}, step.stepKey, (helpers.createEmptyRegistry?.() as any)?.stepBehaviors)
        : {};
      // Merge order is completion-dependent. Non-completed steps: the draft
      // wins over Globus stepData — the draft is cleared on every successful
      // submit (see save()), so an existing draft is strictly newer than the
      // applicant's last submit, and letting stepData win would silently
      // revert edits on Back/Next return while the indicator claims "saved".
      // Completed steps: Globus wins (staff edits land there, and autosave is
      // disabled on completed steps so no fresh draft can exist).
      const merged = isDone(hydrationStep)
        ? { ...engineSeed, ...(draft ?? {}), ...(hydrationStep.stepData ?? {}) }
        : { ...engineSeed, ...(hydrationStep.stepData ?? {}), ...(draft ?? {}) };
      // Hydrate CBC identities/quarantine before mounting the engine so its
      // first customOnChange cannot race the IndexedDB attribution snapshot.
      await hydrateCbc(merged, () => !cancelled);
      if (cancelled || !stepFormMountedRef.current) return;
      // Hydration never relabels: saved provenance is restored only when it
      // matches these exact values; anything else (legacy draft, Globus step
      // data, seed defaults) is 'unknown', not 'manual' and not 'ocr'.
      if (tracksProvenance) provenance.hydrate(merged, captureMeta ?? null);
      setInitial(merged);
      if (isShowcaseStep(step.stepKey)) setScValue(seedShowcaseValue(merged));
    }).catch(onHydrationFailure);
    return () => {
      cancelled = true;
      provenance.invalidateExtractions();
    };
  }, [appId, step.stepKey, step.stepData, step.stepConfig, step.status, provenance, tracksProvenance, createPassportFactory]);

  const uploadShowcasePhoto = useCallback(
    // Gallery photos are in-form media owned by Globus's step-media system, NOT
    // typed documents — uploadShowcaseMedia returns { downloadUrl, fileName }.
    (file: File) => uploadShowcaseMedia(appId, file, { stepKey: step.stepKey, fieldName: 'photo_gallery' }),
    [appId, step.stepKey],
  );

  // Hard-delete a showcase gallery photo before it's spliced out of the
  // gallery — step_media_files has no soft-delete flag, so removal must
  // evict the DB row + GCS object server-side, not just clear the reference.
  const deleteShowcasePhoto = useCallback(
    (ref: { url: string } | string) => deleteShowcaseMedia(typeof ref === 'string' ? ref : ref.url),
    [],
  );

  // Early persistence for the Personal Information step's Profile Picture
  // field (engine 2.41+, onProfilePhotoSelected): the engine's draft autosave
  // does NOT persist this field, only a full step submit does (via the
  // existing data-URL bridge) — so an applicant who picks a photo but never
  // completes the step (closes the tab, another required field is invalid,
  // etc.) silently loses it. Reuse the same account-photo endpoint the My
  // Profile page uses (uploadProfilePhoto -> POST /me/photo) so the photo is
  // durably saved the instant it's picked. Best-effort: swallow failures here
  // and let the existing step-submit bridge retry from the data URL already
  // sitting in the engine's form state; refresh the portal context on success
  // so the sidebar/header avatars pick it up immediately, same as a submit.
  const onProfilePhotoSelected = useCallback(
    async (file: File) => {
      const result = await uploadProfilePhoto(file);
      reloadPortalContext();
      return result;
    },
    [reloadPortalContext],
  );
  const Showcase = useMemo(
    () => (p: any) => <ShowcaseYourself {...p} uploadPhoto={uploadShowcasePhoto} deletePhoto={deleteShowcasePhoto} />,
    [uploadShowcasePhoto, deleteShowcasePhoto],
  );

  // Engine-rendered file fields (file_upload, passport, certificates, and the
  // built-in Skills & Hobbies composite as of engine 2.15) hand the raw File
  // here; the engine persists the returned document reference. Without this
  // prop those uploads fail loud (render-only engine contract). Skills &
  // Hobbies certification files (ctx.path ends in `certificationFile`) route
  // to the dedicated BFF endpoint, which returns a Globus documentId.
  const onUploadDocument = useCallback(
    async (file: File | Blob, ctx: { fieldKey: string; path: (string | number)[] }) => {
      const asFile = file instanceof File ? file : new File([file], ctx.fieldKey, { type: file.type });
      if (ctx.path?.includes('certificationFile')) {
        return uploadSkillCertification(appId, asFile);
      }
      // Globus expects a canonical document-type CODE (e.g. "passport"), NOT the
      // form field key ("passport_upload") — sending the key 400s with
      // "Unknown document type". The served field config carries the code in the
      // field's `displayName`; look it up, falling back to the field key.
      const findCode = (node: any): string | undefined => {
        if (!node || typeof node !== 'object') return undefined;
        if (Array.isArray(node)) {
          for (const n of node) { const r = findCode(n); if (r) return r; }
          return undefined;
        }
        if ((node.id === ctx.fieldKey || node.name === ctx.fieldKey) && typeof node.displayName === 'string' && node.displayName) {
          return node.displayName;
        }
        for (const k of Object.keys(node)) { const r = findCode(node[k]); if (r) return r; }
        return undefined;
      };
      const documentType = findCode(step.stepConfig) || ctx.fieldKey;
      // CBC processing lock, defence-in-depth: the field is already read-only
      // in the engine while the pipeline runs, but an upload that slips
      // through (e.g. a stale render) must not race the in-flight run.
      const lock = cbcUploadLocks.find((l) => l.fieldKey === ctx.fieldKey);
      if (lock) throw new Error(lock.message);
      // Idempotency: a double-click (or double-fired engine event) on the same
      // file must produce ONE upload, not two. Key on field + file identity;
      // the in-flight promise is shared and released when it settles.
      const name = file instanceof File ? file.name : ctx.fieldKey;
      const lastModified = file instanceof File ? file.lastModified : 0;
      const inflightKey = `${ctx.fieldKey}:${name}:${file.size}:${lastModified}`;
      const existing = inflightUploads.current.get(inflightKey);
      if (existing) return existing;
      // Language detection/translation is NOT triggered here: the Hub's
      // server-side pipeline handles both after step submit (see useCbcStatus).
      const promise = uploadDocument(appId, asFile, { documentType, stepKey: step.stepKey, fieldName: ctx.fieldKey });
      inflightUploads.current.set(inflightKey, promise);
      promise.finally(() => {
        if (inflightUploads.current.get(inflightKey) === promise) inflightUploads.current.delete(inflightKey);
      }).catch(() => {});
      return promise;
    },
    [appId, step.stepKey, step.stepConfig, cbcUploadLocks],
  );

  // The engine calls this when the applicant REMOVES a not-yet-approved upload
  // (approved fields are read-only, so this never fires for them). Delete the
  // document from Globus storage so nothing orphans and a re-upload is clean.
  // Best-effort — the engine has already cleared the field; a delete failure
  // must not disrupt the applicant.
  // In-flight upload dedupe map for onUploadDocument (double-click guard).
  const inflightUploads = useRef(new Map<string, ReturnType<typeof uploadDocument>>());

  const onDeleteDocument = useCallback(
    async (ref: unknown, ctx?: { fieldKey?: string }) => {
      // Removal invalidates the request independently of values-only snapshots.
      if (typeof ctx?.fieldKey === 'string' && ctx.fieldKey.includes('passport')) {
        // Any extraction still in flight for the removed document is now
        // obsolete; its OCR provenance goes with the cleared values.
        onPassportDocumentRemoved(latestFormValuesRef.current);
      }
      // A media UUID is NOT a document id. Route by the canonical reference
      // before considering the legacy object's id/documentId properties.
      const mediaRef = ref && typeof ref === 'object' ? ref as Record<string, unknown> : undefined;
      const mediaId = stepMediaIdFromUrl(ref) ||
        stepMediaIdFromUrl(mediaRef?.downloadUrl) || stepMediaIdFromUrl(mediaRef?.url);
      if (mediaId) {
        try {
          await deleteStepMedia(mediaId);
        } catch {
          // The renderer may already clear the field or swallow a rejected
          // deletion callback. Keep an explicit host notice; never fall back
          // to the documents route or retry the deletion automatically.
          showPageError('The uploaded file could not be removed. Please check it before continuing.');
        }
        return;
      }
      const fromUrl = (u: unknown) => {
        if (typeof u !== 'string') return undefined;
        const m = u.match(/\/documents\/([^/]+)\/download/);
        return m ? m[1] : undefined;
      };
      let docId: string | undefined;
      if (typeof ref === 'string') docId = fromUrl(ref);
      else if (ref && typeof ref === 'object') {
        const o = ref as Record<string, unknown>;
        docId = (typeof o.documentId === 'string' && o.documentId) ||
          (typeof o.id === 'string' && o.id) ||
          fromUrl(o.downloadUrl) || fromUrl(o.url) || undefined;
      }
      if (!docId) return;
      try {
        await portal.deleteDocument(appId, docId);
      } catch {
        // Globus 409s on an approved doc and 404s on an already-gone one — both
        // are fine to ignore here (the field is already cleared).
      }
    },
    [appId, onPassportDocumentRemoved],
  );

  // One click on Save & Continue makes the engine call onSave then, for steps
  // with substantial data, onComplete — both wired to this fn (onSave alone
  // persists partial steps; onComplete carries the required-field gate and the
  // engine's early-return semantics). Dedupe identical back-to-back payloads so
  // a single click submits to Globus once, not twice. A failed attempt clears
  // the cache so retries always re-POST.
  const lastSubmit = useRef<{ key: string; navigate: boolean; promise: Promise<unknown> } | null>(null);
  // `navigate: false` = upload-triggered CBC auto-submit: identical pipeline
  // (freeze, drain, submit, clear draft, open the CBC polling window) but the
  // applicant stays on the step (onAutoSubmitted refreshes without goTo).
  // The dedupe key is the payload alone — auto and manual submissions of the
  // same data share ONE POST. A manual Save & Continue that lands while (or
  // after) an identical auto-submit is in flight JOINS it: no second POST,
  // and the navigation the applicant asked for is chained exactly once onto
  // the shared submit.
  const save = (data: Record<string, unknown>, opts: { navigate?: boolean } = {}) => {
    if (!isCurrentForm()) return Promise.reject(new Error('This application has changed. Please reload the step.'));
    const navigate = opts.navigate !== false;
    const key = `${appId}:${step.stepKey}:${JSON.stringify(data)}`;
    const existing = lastSubmit.current;
    if (existing?.key === key) {
      if (navigate && !existing.navigate) {
        // Manual continuation of an upload-triggered submit: reuse the
        // in-flight (or completed) POST, add the navigation once, and store
        // the chained promise so further identical clicks dedupe onto it.
        const chained = existing.promise.then(async (r) => {
          if (!isCurrentForm()) return r;
          await onSubmitted();
          return r;
        });
        lastSubmit.current = { key, navigate: true, promise: chained };
        chained.catch(() => {
          // Underlying submit failed (its own catch already unfroze autosave)
          // or navigation refresh threw — allow a clean retry.
          if (lastSubmit.current?.promise === chained) lastSubmit.current = null;
        });
        return chained;
      }
      return existing.promise;
    }
    // Capture the exact values/evidence pair synchronously, before draining
    // autosaves. Edits arriving during that await belong to a later snapshot.
    const fullProvenance = tracksProvenance ? provenance.toSaveMetadata(data) : null;
    const fullSnapshot = tracksProvenance ? provenance.snapshot(data) : null;
    // Old schema aliases remain in live/draft history. Only final wire copies
    // and their successful receipt follow this version's unique served target.
    let prepared: ReturnType<typeof prepareCanonicalPassportSubmission> | null = null;
    try {
      if (fullSnapshot) prepared = prepareCanonicalPassportSubmission(
        data, fullProvenance, fullSnapshot,
        canonicalPassportTargets((step.stepConfig as { fieldsConfig?: unknown } | undefined)?.fieldsConfig),
      );
    } catch (error) {
      return Promise.reject(error);
    }
    const submitData = prepared?.responses ?? data;
    const submitProvenance = prepared?.metadata ?? null;
    const acceptedSnapshot = prepared?.receipt ?? null;
    const recordAcceptedSave = () => tracksProvenance
      ? drafts.accept(appId, step.stepKey, acceptedSnapshot)
      : drafts.clear(appId, step.stepKey);
    const promise = (async () => {
      // Freeze the autosave pipelines for the duration of the submit (bug #1):
      // cancel the pending showcase debounce, invalidate its in-flight
      // indicator sequence, and let any in-flight server draft settle first so
      // the Hub can never apply a stale draft AFTER this submit.
      orchestrator.freeze();
      if (scSaveTimer.current) {
        clearTimeout(scSaveTimer.current);
        scSaveTimer.current = null;
      }
      scSaveSeq.current++;
      // Let EVERY outstanding draft POST settle (drafts issued while this
      // loop runs are impossible — orchestrator.freeze() gates all producers).
      await orchestrator.drainPendingDrafts();
      if (!isCurrentForm()) throw new Error('This application has changed. Please reload the step.');
      let result: Awaited<ReturnType<typeof portal.submitStep>>;
      // Capture evidence travels with the same payload it describes. It is
      // only ever attached on passport steps and only when tracked values exist.
      const submitOnce = () =>
        submitProvenance
          ? portal.submitStep(appId, step.stepKey, submitData, submitProvenance)
          : portal.submitStep(appId, step.stepKey, submitData);
      try {
        result = await submitOnce();
      } catch (e) {
        // A true network/timeout failure (classifySaveError's `network` code —
        // fetch never got a response) is often transient, e.g. a concurrent
        // document upload contending for the same connection (Asana
        // 1217982476199692). Retry once before letting it reach the caller's
        // catch, which is the only case where surfacing the generic "check
        // your connection" toast on the FIRST failure is a false alarm — every
        // other classifySaveError code is a real, structured rejection that
        // must not be retried silently.
        if (classifySaveError(e).code !== 'network') throw e;
        await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
        if (!isCurrentForm()) throw e;
        result = await submitOnce();
      }
      if (!isCurrentForm()) {
        // The old POST may have been accepted, but returning A→B→A could have
        // created a NEW draft at the same key. Never mutate local storage from
        // an obsolete form lifetime, nor notify/navigate another application.
        return result;
      }
      // Establish awaiting-review state as soon as the upstream submit itself
      // succeeds. Draft cleanup and refresh are ancillary; neither should make
      // a stale rejection reappear after Globus already accepted the correction.
      onSubmitAccepted?.();
      // Refresh the quarantine lock timers (a manual retry after an auto-submit
      // failure restarts the temporary processing lock while the attribution
      // quarantine remains intact).
      cbcOnSubmitSuccess();
      await recordAcceptedSave();
      if (!isCurrentForm()) return result;
      // Edits are now persisted upstream: the pending-edit flags are consumed
      // (origins stay). Queries depending on this save (identity annotations
      // on Personal/Passport Information) are invalidated in onSubmitted —
      // only after the confirmed save, never optimistically.
      if (tracksProvenance) provenance.markSaved();
      if (tracksProvenance) reloadPortalContext();
      track('step_submitted', { stepKey: step.stepKey });
      // Open the Hub-pipeline polling window: a submitted background-check
      // step kicks off server-side language detection + translation, and the
      // status record appears asynchronously. Free for non-CBC steps.
      cbc.markSubmitted();
      // Same async-record pattern for the SWT enrollment-proof pipeline: the
      // Hub auto-triggers the assessment on upload/submit and the status
      // record appears asynchronously. Free for non-SWT steps.
      if (isSwtProofStep) swtProof.markSubmitted();
      showPageError(undefined);
      setNotice('Saved.');
      // Awaited: navigation happens inside, AFTER the step-access snapshot
      // refresh (bug #2) — the engine's spinner covers the whole window.
      // Auto-submits refresh WITHOUT navigating (onAutoSubmitted).
      if (navigate) await onSubmitted();
      else await onAutoSubmitted?.();
      // A submitted Profile Picture becomes the ACCOUNT photo (the BFF
      // bridges it to Globus) — refresh the portal context so the sidebar,
      // header and drawer avatars pick it up without a full page reload.
      if (
        Object.entries(data).some(
          ([k, v]) => /photo|picture/i.test(k) && typeof v === 'string' && v.startsWith('data:image/'),
        )
      ) {
        reloadPortalContext();
      }
      return result;
    })();
    lastSubmit.current = { key, navigate, promise };
    promise.catch(() => {
      // A failed submit re-enables autosave — the applicant keeps editing.
      orchestrator.unfreeze();
      if (lastSubmit.current?.promise === promise) lastSubmit.current = null;
    });
    return promise;
  };
  // Wire the CBC auto-submit ref to the current save closure. Updated on each
  // render so the lifecycle hook always calls the latest closure — the ref
  // pattern avoids a dependency cycle between the hook call and save().
  cbcAutoSubmitRef.current = (values: Record<string, unknown>) => save(values, { navigate: false });

  if (!initial) return <div className="card formcard skeleton" role={errorNotice ? 'alert' : undefined}>{errorNotice ?? 'Loading step…'}</div>;
  // Format the scheduled-unlock timestamp for display. The portal never
  // computes whether the lock has expired from this value — it is purely
  // informational, rendered in the applicant's local timezone.
  const unlockAtFormatted = unlockAt
    ? new Date(unlockAt).toLocaleString(undefined, {
        dateStyle: 'long',
        timeStyle: 'short',
      })
    : undefined;
  // Agreement hydration pins one published version and mirrors its metadata
  // both beside fieldsConfig and inside fieldsConfig.agreement. Accept either
  // shape so the applicant always sees the exact version they are signing.
  const stepConfig = step.stepConfig as {
    agreement?: { agreementCode?: string; versionLabel?: string };
    fieldsConfig?: { agreement?: { agreementCode?: string; versionLabel?: string } };
  } | undefined;
  const agreementMeta = stepConfig?.agreement ?? stepConfig?.fieldsConfig?.agreement;
  const isAgreementStep = Boolean(agreementMeta?.agreementCode);
  const agreementVersionLabel = agreementMeta?.versionLabel;
  const lockCopy = lockBannerCopy({
    bannerMessage: lockBannerMessage,
    reason: lockReason,
    viewOnly,
    unlockConditions,
    resolveTitle: resolveStepTitle,
  });
  return (
    <div className="card formcard">
      {/* Description intentionally omitted — the engine renders it in the form body. */}
      <header className="formhead">
        <span className="stepcount">Step {position.n} of {position.of}</span>
        <h2>{step.title}</h2>
      </header>
      {identityStepKind ? (
        <IdentityAnnotationsNotice annotations={identityAnnotations} stepKind={identityStepKind} />
      ) : null}
      {/* Section-lock banner: rendered verbatim from the staff-authored message.
          Time-window locks additionally surface the scheduled auto-release time.
          The portal never derives lock state — it only renders what the server
          sent in the step-access snapshot (Nexus section-locking contract). */}
      {/* Every locked/view-only step gets a banner: the staff-authored message
          verbatim when one exists, otherwise a per-reason-code fallback (bug
          #9 — a null banner_message must never leave a silently dead form). */}
      {readOnly && (
        <div className="globus-banner" role="status">
          {lockCopy.headline}
          {lockCopy.condition && <span className="lock-unlock-condition"> {lockCopy.condition}</span>}
          {unlockAtFormatted && (
            <span className="lock-unlock-at"> This section will unlock on {unlockAtFormatted}.</span>
          )}
        </div>
      )}
      {step.globusMessage && !(step.needsAction && step.rejectionMessage) && (
        <div className="globus-banner" role="status">{step.globusMessage}</div>
      )}
      {/* Applicant-facing review message comes only from rejection_message.
          rejectionCode/reviewer labels are never expanded into display copy. */}
      {!hideReviewBanner && step.needsAction && !suppressNeedsAction && step.rejectionMessage && (
        <div className="globus-banner review-feedback-banner" role="alert">
          <strong>Changes requested</strong>
          <div>{step.rejectionMessage}</div>
        </div>
      )}
      {!hideReviewBanner && step.needsAction && suppressNeedsAction && (
        <div className="globus-banner ok" role="status">
          <strong>Submitted for review</strong>
        </div>
      )}
      {/* Hub compliance feedback (Camp Contract duration) renders exactly like
          a staff rejection: same banner, only on this step, stepper badge +
          incomplete state come from needsAttention() in lib/steps.ts. The
          form itself stays editable (only the correction fields unlock). */}
      {campDurationFeedback && (
        <div
          className="globus-banner review-feedback-banner camp-contract-compliance-alert"
          role="alert"
          data-testid="camp-contract-duration-alert"
        >
          <strong>Contract dates need attention</strong>
          <div>{CAMP_DURATION_APPLICANT_MESSAGE}</div>
          {campDurationFeedback.freshness === 'stale' && (
            <small>This evaluation may be out of date.</small>
          )}
        </div>
      )}
      {/* CBC processing lock notice: the upload field below is read-only while
          the Hub pipeline processes the submitted document — say so, in
          applicant-safe copy, instead of leaving a silently dead upload area.
          One banner even if multiple fields are locked (same message). */}
      {cbcUploadLocks.length > 0 && !readOnly && (
        <div className="globus-banner" role="status" data-testid="banner-cbc-processing">
          {cbcUploadLocks[0].message}
        </div>
      )}
      {cbcProcessingDelays.length > 0 && !readOnly && (
        <div className="globus-banner" role="status" data-testid="banner-cbc-processing-delayed">
          {CBC_PROCESSING_DELAYED_NOTICE}
        </div>
      )}
      {cbcReviewNotice && (
        <div className="globus-banner" role="status" data-testid="banner-cbc-review-required">
          {cbcReviewNotice}
        </div>
      )}
      {cbcTranslationFallback && !readOnly && (
        <CbcTranslationFallbackCard
          requirementId={cbcTranslationFallback.requirementId}
          detectedLanguage={cbcTranslationFallback.detectedLanguage}
          onUploaded={onCbcTranslationUploaded}
          onError={onCbcPersistError}
        />
      )}
      {notice && <div className="globus-banner ok" role="status">{notice}</div>}
      {errorNotice && <div ref={errorBannerRef} className="globus-banner err" role="alert" tabIndex={-1}>{errorNotice}</div>}
      {/* SWT status panel (Proof of Student Status step only): applicant-safe
          enrollment-proof pipeline state + professor/university verification
          state. Never blocks the form — display only. */}
      {isSwtProofStep && (
        <SwtStatusPanel
          assessment={swtProof.assessment}
          vacationVerification={swtAcademic.vacationVerification}
          requests={swtAcademic.academicVerificationRequests}
        />
      )}
      {showcase ? (
        <div className="showcase-step">
          <ShowcaseYourself
            value={scValue}
            isReadOnly={readOnly}
            onChange={(partial) =>
              setScValue((prev) => {
                const next = { ...prev, ...(partial as Partial<ShowcaseValue>) };
                scDirty.current = true;
                void drafts.save(appId, step.stepKey, next as Record<string, unknown>);
                return next;
              })
            }
            onValidationChange={setScValid}
            showValidationErrors={scShowErrors}
            uploadPhoto={uploadShowcasePhoto}
            deletePhoto={deleteShowcasePhoto}
          />
          <p className="formactions" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            {onBack ? (
              <button type="button" style={{ width: 'auto' }} onClick={scFlushThenNavigate(onBack)} data-testid="button-step-back">
                Back
              </button>
            ) : (
              <span />
            )}
            {canSubmit && <span style={{ display: 'flex', gap: 12 }}>
              {/* Enabled even when incomplete: a disabled button gives no reason.
                  Clicking while incomplete surfaces the requirements (escalates the
                  checklist to an error state) instead of silently doing nothing. */}
              <button
                type="button"
                className="primary"
                style={{ width: 'auto' }}
                onClick={() => {
                  setScShowErrors(true);
                  if (!scValid) return;
                  void save(scValue as Record<string, unknown>).catch((e: unknown) => {
                    // STEP_LOCKED here means the section was locked server-side
                    // between load and this click — same recovery as the form
                    // engine's onError below: refresh the step-access snapshot.
                    if (isStepLockedError(e)) onAccessRefresh?.();
                    const cls = classifySaveError(e);
                    showSubmitError(cls.message);
                    // Previously untracked (Asana 1217982476199692): this catch
                    // is the only failure signal for the Showcase manual-save
                    // path, and it had none — a recurrence here was invisible
                    // in PostHog unless the applicant filed a ticket.
                    track('step_error', { stepKey: step.stepKey, ctx: 'manual_save', code: cls.code, message: (e as Error)?.message });
                  });
                }}
              >
                {isLast ? 'Save' : 'Save & Continue'}
              </button>
            </span>}
          </p>
          {!isDone(step) && (
            <p className="muted" role="status" aria-live="polite" style={{ textAlign: 'right', fontSize: 12, marginTop: 8 }}>
              {scSaveStatus === 'idle' && 'Your progress is saved automatically.'}
              {scSaveStatus === 'saving' && 'Saving…'}
              {scSaveStatus === 'saved' && 'Progress saved automatically · Saved just now'}
              {scSaveStatus === 'error' &&
                (scSaveErrorMessage ??
                  "We couldn't save your latest changes — check your connection. Your work will retry saving as you type.")}
            </p>
          )}
        </div>
      ) : (
      <>
      {warning && <div className="globus-banner" role="alert">{warning}</div>}
      <div className={isAgreementStep ? 'agreement-document-step' : undefined}>
        <Suspense fallback={<p>Loading form…</p>}>
          {(() => {
          const engine = (
            <FormEnginePortable
              onFieldChange={onPassportFieldChange}
              theme={scheme}
              stepId={step.stepKey}
              globusApplicationId={String(appId)}
              // Program code (e.g. "CAMP_WP"), not the Globus program UUID —
              // brand/program-gated step copy (e.g. ReferenceDetailsStep's
              // referee-suitability disclaimer) keys off this.
              programId={programCode}
              hideTitle
              currentStepNumber={position.n}
              totalSteps={position.of}
              registry={registry}
              stepConfig={bindApplicationTemplateDownloads({
                // Embassy options (once loaded) are injected into a clone of
                // step.stepConfig — prefer that clone so embassy_city fields carry
                // their options; fall back to the raw stepConfig otherwise.
                ...((embassyConfig as Record<string, unknown> | undefined) ??
                  (step.stepConfig as Record<string, unknown> | undefined) ?? {
                  stepKey: step.stepKey,
                  name: step.title,
                  fieldsConfig: null,
                }),
                // BOP's stepConfig.stepType is a workflow-structural value (form/hub/
                // sub_task) — the engine's inline SWT eligibility hook overloads the
                // same field name for a distinct semantic flag it checks for equality
                // against "proof_of_status"/"camp_contract". Override just for this
                // one step so the hook activates without touching BOP's field.
                stepType:
                  step.stepKey === 'proof_of_student_status_v1_0'
                    ? 'proof_of_status'
                    : ((step.stepConfig as { stepType?: string } | undefined)?.stepType ??
                      // Defensive: the BFF stamps stepType 'hub' from the workflow
                      // snapshot, but a hub row identified by nodeType alone (e.g.
                      // a stale cached stepConfig) must still render the checklist
                      // — the engine gates HubOverview on stepType === 'hub'.
                      (isHubStep(step) ? 'hub' : undefined)),
              }, String(appId))}
              // Hub checklist page data — presence + stepType 'hub' flips the
              // engine from form rendering to its HubOverview (engine contract
              // v2.31.0; hub steps typically carry no fieldsConfig of their own).
              {...(hubChecklist ? { hubChecklist } : {})}
              // autoSave was silently OFF since launch: the engine gates its
              // debounced autosave on this flag (default {}), which was never set
              // — so the onAutoSave pipeline below never fired for engine steps.
              // Disabled on COMPLETED steps deliberately: a completed step's
              // re-edit only persists via the validated Save & Continue, and the
              // draft-merge keeps Globus-wins there — an autosave indicator
              // claiming "saved" would be a lie. languageAnalysis/translation
              // enable the AP-context-driven document-language affordances.
              featureFlags={{
                eligibilityValidation: true,
                languageAnalysis: true,
                translation: true,
                autoSave: !isDone(step) && !readOnly,
              }}
              readOnly={readOnly}
              eligibilityApiFetch={eligibilityApiFetch}
              stepProgress={stepProgress}
              personalInfoData={personalStepData ? { stepData: personalStepData } : undefined}
              initialFormData={initial}
              isLastStep={isLast}
              showcaseComponent={Showcase}
              onUploadDocument={onUploadDocument}
              onProfilePhotoSelected={onProfilePhotoSelected}
              onDeleteDocument={onDeleteDocument}
              resolveDocumentUrl={resolveStepDocumentUrl}
              fieldComponents={{ info: InfoFieldWithTemplateDownload, info_text: InfoFieldWithTemplateDownload }}
              fieldPermissions={{ readOnlyFields }}
              expectedLegalName={expectedLegalName}
              expectedPassportName={expectedPassportName}
              // Sponsor/program-team review feedback for Skills & Hobbies composite.
              // Passed only when the step has feedback items; the engine ignores the
              // prop on non-SkillsHobbies steps (it is not in their prop surface).
              {...(isSkillsHobbiesStep(step.stepKey) && reviewItems.length
                ? { sponsorReviewItems: reviewItems }
                : {})}
              onBack={onBack}
              backLabel="Back"
              {...(canSubmit
                ? {
                    onSave: (data: Record<string, unknown>) => save(data),
                    onComplete: (data: Record<string, unknown>) => save(data),
                  }
                : {})}
              // Track the latest form values on every engine change so
              // flush() (called by navigateSafe on non-submit navigation)
              // can write them to IDB even if the autosave debounce hasn't
              // fired yet. This is the primary defence against photo loss
              // on stepper, phase-tab, and mobile navigation.
              // Track latest form values synchronously on every change so
              // flush() (called by navigateSafe on non-submit navigation)
              // can guarantee the current photo data URL is in IDB even if
              // the autosave debounce hasn't fired yet (task #15 core fix).
              customOnChange={(data: Record<string, unknown>) => {
                const values = reconcileFormValues(data);
                if (!values) return;
                // Track latest form values for flush() and getCbcFieldFile usage in the hook.
                latestFormValuesRef.current = values;
                // Reconcile the live form, not a possibly stale outer watch
                // snapshot after a synchronous correction. No invented event.
                if (tracksProvenance) provenance.observeValues(values);
                // Delegate all CBC-specific observation to the lifecycle hook.
                // Detects new/removed CBC identities, manages quarantine, and
                // fires the auto-submit pipeline when a new file lands.
                onCbcFieldChange(values);
              }}
              // orchestrator.onAutoSave handles the full IDB→server pipeline
              // with race-safety (task #14): freezes on submit, drains before
              // submitting, strips data: URLs from the server payload via the
              // orchestrator's saveDraft callback (stripDataUrls applied there).
              onAutoSave={orchestrator.onAutoSave}
              onError={(e: Error, ctx: string) => {
                setNotice(undefined);
                // The engine surfaces field validation inline and reports
                // save failures itself; keep the Portal's classified server
                // reason here so specific rejections remain actionable.
                if (ctx === 'save' || ctx === 'complete') {
                  // Save failures the applicant can actually fix themselves get the
                  // real reason — classified from the server's structured
                  // ApiError.code/.status (Asana 1217767958880539) instead of a
                  // regex/allowlist over the message text, which silently
                  // collapsed every unrecognised rejection (STEP_LOCKED included,
                  // since isStepLockedError never matched — the message never
                  // contains that token) into one misleading "check your
                  // connection" banner. STEP_LOCKED additionally refreshes the
                  // step-access snapshot so the lock banner appears and the form
                  // flips to read-only without a full page reload.
                  if (isStepLockedError(e)) onAccessRefresh?.();
                  showSubmitError(classifySaveError(e).message);
                } else {
                  // Keep a summary for screen readers and validation paths without
                  // an inline surface. The engine already shows the field message
                  // and scrolls to it; do not redirect that scroll back up here.
                  showFieldValidationNotice(e.message || 'Please fix the highlighted fields before continuing.');
                }
                // `code` is undefined for validation-context blocks (classifySaveError
                // only applies to save/complete) — still useful to have `message`-only
                // rows for those in PostHog, so don't skip tracking them.
                const code = ctx === 'save' || ctx === 'complete' ? classifySaveError(e).code : undefined;
                track('step_error', { stepKey: step.stepKey, ctx, code, message: e.message });
              }}
            />
          );
          // AP-style context: language detection results + auto-translation
          // state, merged over the engine's default (no-op) context. Without
          // the provider the engine's translation affordances are inert.
          if (!apProvider) return engine;
          const { Provider, defaultContext } = apProvider;
          return <Provider value={lang.buildContext(defaultContext)}>{engine}</Provider>;
          })()}
        </Suspense>
        {isAgreementStep && agreementVersionLabel && (
          <small className="agreement-published-version" data-testid="agreement-published-version">
            Agreement published version: {agreementVersionLabel}
          </small>
        )}
      </div>
      </>
      )}
    </div>
  );
});
